import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import CKEditorWithSource from "./CKEditorWithSource";
import axios from "axios";
import config from "../coreFIles/config";
import { getAdminAuthToken } from "../coreFIles/auth";
import {
  adminCourseGetAction,
  adminCourseWeekContentGetAction,
  adminCourseWeekContentUpdateAction,
  adminCourseWeekSectionCreateFormDataAction,
  adminCourseWeekSectionUpdateFormDataAction,
  adminCourseWeekSectionsListAction,
  adminWeekSectionsAvailableAction,
  adminWeekSectionCreateAction,
  adminWeekSectionUpdateAction,
  adminSectionMediaCreateAction,
  adminSectionMediaUpdateAction,
  adminSectionMediaDeleteAction,
  adminSectionMediaListAction
} from "../Action/action";

function formatDateShort(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "2-digit" });
  } catch (e) {
    return "";
  }
}

function sectionMediaProviderFromRow(m) {
  return String(m?.provider || "").toLowerCase() === "thirdparty" ? "Thirdparty" : "Selfhosted";
}

function sectionMediaPositionDigitsOnly(value) {
  return String(value ?? "").replace(/\D/g, "");
}

function sectionMediaPositionFormValueFromRow(m) {
  if (m?.position == null || m.position === "") return "";
  return sectionMediaPositionDigitsOnly(String(m.position));
}

function sectionMediaPositionIntForSave(digitsState) {
  const s = String(digitsState ?? "").trim();
  if (s === "") return 0;
  const n = parseInt(sectionMediaPositionDigitsOnly(s), 10);
  return Number.isFinite(n) ? n : 0;
}

function videoOrientationFormValue(value) {
  return String(value || "").toLowerCase() === "portrait" ? "portrait" : "landscape";
}

function videoTypeFormValue(value) {
  const v = String(value || "").trim();
  if (v === "1" || v === "2" || v === "3") return v;
  const lower = v.toLowerCase();
  if (lower.includes("intro")) return "1";
  if (lower.includes("core")) return "3";
  if (lower.includes("course")) return "2";
  return "2";
}

function resolvePublicUrl(pathOrUrl) {
  if (!pathOrUrl) return "";
  const s = String(pathOrUrl);
  if (/^https?:\/\//i.test(s)) return s;
  let origin = "";
  try {
    if (config.apiUrl) origin = new URL(config.apiUrl).origin;
  } catch (e) {
    origin = "";
  }
  if (!origin) origin = "http://localhost:3001";
  if (s.startsWith("/")) return `${origin}${s}`;
  return `${origin}/${s}`;
}

function editorPlainText(html) {
  return String(html || "")
    .replace(/&nbsp;/g, " ")
    .replace(/<[^>]*>/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function buildApiUrl(endpoint) {
  const base = (config.apiUrl || "").replace(/\/+$/, "");
  const path = String(endpoint || "").replace(/^\/+/, "");
  return base ? `${base}/${path}` : `/${path}`;
}

class CkUploadAdapter {
  constructor(loader) {
    this.loader = loader;
    this.controller = new AbortController();
  }
  abort() {
    try {
      this.controller.abort();
    } catch (e) {}
  }
  async upload() {
    const file = await this.loader.file;
    const fd = new FormData();
    fd.append("upload", file);
    const token = getAdminAuthToken();
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    const resp = await axios.post(buildApiUrl("/admin/upload/image"), fd, {
      headers,
      withCredentials: true,
      signal: this.controller.signal
    });
    const url = resp?.data?.url;
    if (!url) throw new Error("Upload failed");
    return { default: resolvePublicUrl(url) };
  }
}

function CkUploadAdapterPlugin(editor) {
  // eslint-disable-next-line no-param-reassign
  editor.plugins.get("FileRepository").createUploadAdapter = (loader) => new CkUploadAdapter(loader);
}

function audienceKeyShort(audience) {
  const a = String(audience || "").toLowerCase();
  if (a === "adult") return "(A)";
  if (a === "kids") return "(K)";
  if (a === "both") return "(A + K)";
  return "";
}

function formatBytes(bytes) {
  const n = Number(bytes);
  if (!Number.isFinite(n) || n <= 0) return "";
  const units = ["B", "KB", "MB", "GB"];
  let v = n;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i += 1;
  }
  const out = i === 0 ? String(Math.round(v)) : v.toFixed(1);
  return `${out} ${units[i]}`;
}

function sectionColorClass(title) {
  const t = String(title || "").toLowerCase();
  if (t.includes("course video") || t.includes("core videos")) return "orange";
  if (t.includes("habits")) return "green";
  return "blue";
}

const HUB_SECTION_TYPES = ["parent_guides", "play_to_practice", "hhh_hub"];

function isHubSectionType(type) {
  return HUB_SECTION_TYPES.includes(String(type || "").trim().toLowerCase());
}

function normalizeAdminSectionType(raw) {
  const t = String(raw || "").trim().toLowerCase();
  if (t === "content" || t === "resources" || isHubSectionType(t)) return t;
  return "videos";
}

function defaultVisibilityForType(type, current) {
  if (isHubSectionType(type) && (!current || current === "all")) return "preview";
  return current || "all";
}

function emptySectionForm() {
  return {
    title: "",
    audience: "Both",
    type: "videos",
    content: "",
    file_url: "",
    thumbnail_url: "",
    is_core_videos: false,
    is_complete_section: false,
    video_type: "",
    visibility: "all",
    display_title: "",
    display_subtitle: ""
  };
}

const VIDEO_MEDIA_GROUPS = [
  { value: "", label: "None (live / untagged)" },
  { value: "intro", label: "Intro video" },
  { value: "parent_guides_video", label: "Parent Guides hero" },
  { value: "activities_video", label: "Play to Practice hero" },
  { value: "hhh_hero_video", label: "HHH hub hero" },
  { value: "hhh_child_video", label: "HHH child video" }
];

const RESOURCE_MEDIA_GROUPS = [
  { value: "", label: "None (use reading type)" },
  { value: "essential_child", label: "Essential — For Child" },
  { value: "priority_adult", label: "Priority — For Adult" },
  { value: "optional_guides", label: "Optional Guides" },
  { value: "essential_activities", label: "Essential Activities" },
  { value: "optional_activities", label: "Optional Activities" },
  { value: "hhh_parent_guide", label: "HHH Parent Guide" }
];

function mediaGroupLabel(value) {
  const v = String(value || "").trim().toLowerCase();
  if (!v) return "";
  const hit = [...VIDEO_MEDIA_GROUPS, ...RESOURCE_MEDIA_GROUPS].find((o) => o.value === v);
  return hit ? hit.label : v;
}

function defaultVideoMediaGroup(ws) {
  const t = String(ws?.section_type || ws?.type || "").toLowerCase();
  const title = String(ws?.section_title || "").toLowerCase();
  if (t === "parent_guides" || title.includes("parent guide")) return "parent_guides_video";
  if (t === "play_to_practice" || title.includes("play to practice")) return "activities_video";
  if (t === "hhh_hub" || title.includes("healthy home habit")) return "hhh_hero_video";
  if (title.includes("intro video") || Number(ws?.video_type) === 1) return "intro";
  return "";
}

function isDocumentLikeFile(fileType, fileUrl) {
  const ft = String(fileType || "").toLowerCase();
  const url = String(fileUrl || "").split("?")[0].toLowerCase();
  if (
    ft === "pdf" ||
    ft.includes("pdf") ||
    ft.includes("image") ||
    ft.includes("png") ||
    ft.includes("jpg") ||
    ft.includes("jpeg") ||
    ft.includes("webp") ||
    ft.includes("gif") ||
    ft.includes("doc") ||
    ft.includes("xls")
  ) {
    return true;
  }
  return /\.(pdf|png|jpe?g|webp|gif|docx?|xlsx?)$/i.test(url);
}

function isVideoLikeFile(fileType, fileUrl) {
  const ft = String(fileType || "").toLowerCase();
  const url = String(fileUrl || "").split("?")[0].toLowerCase();
  if (ft === "video" || ft.includes("mp4") || ft.includes("webm") || ft.includes("mov") || ft.includes("m3u8")) {
    return true;
  }
  return /\.(mp4|m4v|webm|mov|m3u8)$/i.test(url);
}

function isVideoMedia(m) {
  if (isDocumentLikeFile(m?.file_type, m?.file_url)) return false;
  if (isVideoLikeFile(m?.file_type, m?.file_url)) return true;
  return String(m?.media_kind || "").toLowerCase() === "video";
}

function sectionTypeOptions() {
  return (
    <>
      <option value="videos">Videos</option>
      <option value="resources">Resources</option>
      <option value="content">Content</option>
      <option value="parent_guides">Parent Guides (hub)</option>
      <option value="play_to_practice">Play to Practice (hub)</option>
      <option value="hhh_hub">Healthy Home Habits (hub)</option>
    </>
  );
}

export default function WeekContent() {
  const { courseId, weekId } = useParams();

  const [loading, setLoading] = useState(true);
  const [savingWeek, setSavingWeek] = useState(false);
  const [savingSection, setSavingSection] = useState(false);
  const [error, setError] = useState("");
  const [weekFieldErrors, setWeekFieldErrors] = useState({});

  const [courseTitle, setCourseTitle] = useState("Course");
  const [week, setWeek] = useState({
    title: "",
    subtitle: "",
    heading_title: "",
    description_title: "",
    short_description: "",
    description: "",
    icon: "",
    week_image: ""
  });
  const [uploadingIcon, setUploadingIcon] = useState(false);
  const [uploadingWeekImage, setUploadingWeekImage] = useState(false);
  const iconInputRef = useRef(null);
  const weekImageInputRef = useRef(null);

  const [weekSections, setWeekSections] = useState([]);
  const [activeWeekSectionId, setActiveWeekSectionId] = useState(null);

  const [mediaBySectionId, setMediaBySectionId] = useState({});
  const [loadingMediaBySectionId, setLoadingMediaBySectionId] = useState({});

  const [addSectionModalOpen, setAddSectionModalOpen] = useState(false);
  const [availableSections, setAvailableSections] = useState([]);
  const [allSections, setAllSections] = useState([]);
  /** All `week_sections` rows for this course week (`all_for_week=1`); used for add/edit title datalists */
  const [weekSectionDatalistSections, setWeekSectionDatalistSections] = useState([]);
  const [loadingAvailableSections, setLoadingAvailableSections] = useState(false);
  const [addSectionForm, setAddSectionForm] = useState(() => emptySectionForm());
  const [editSectionForm, setEditSectionForm] = useState(() => emptySectionForm());
  const [addSectionPdfFile, setAddSectionPdfFile] = useState(null);
  const [editSectionPdfFile, setEditSectionPdfFile] = useState(null);
  const [addSectionPdfError, setAddSectionPdfError] = useState("");
  const [editSectionPdfError, setEditSectionPdfError] = useState("");
  const addSectionPdfInputRef = useRef(null);
  const editSectionPdfInputRef = useRef(null);
  const [uploadingSectionThumb, setUploadingSectionThumb] = useState(false);
  const addSectionThumbInputRef = useRef(null);
  const editSectionThumbInputRef = useRef(null);

  const [editSectionModalOpen, setEditSectionModalOpen] = useState(false);
  /** course_week_sections.id pinned when Edit opens — must not follow scroll-spy / left-nav selection */
  const [editingWeekSectionId, setEditingWeekSectionId] = useState(null);

  // Wireframe modals
  const [videoPreviewOpen, setVideoPreviewOpen] = useState(false);
  const [videoModalOpen, setVideoModalOpen] = useState(false);
  const [resourceModalOpen, setResourceModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);

  const [videoEditingId, setVideoEditingId] = useState(null);
  const [resourceEditingId, setResourceEditingId] = useState(null);
  const [currentMediaSectionId, setCurrentMediaSectionId] = useState(null);

  const [videoPreview, setVideoPreview] = useState({ title: "", description: "", duration: "0:00", section: "", uploadedLabel: "" , url: ""});
  const [videoForm, setVideoForm] = useState({
    title: "",
    type: "",
    description: "",
    duration: "",
    position: "",
    video_orientation: "landscape",
    provider: "Selfhosted",
    file_url: "",
    mediaGroup: ""
  });
  const videoEditSourceRef = useRef(null);
  const [resourceForm, setResourceForm] = useState({ name: "", fileType: "PDF", status: "Active", description: "", position: "", isEditable: false, readingType: "priority", mediaGroup: "" });
  const [deleteTarget, setDeleteTarget] = useState({ kind: "", id: null, name: "" });

  const [videoExistingFileUrl, setVideoExistingFileUrl] = useState("");
  const [videoExistingThumbUrl, setVideoExistingThumbUrl] = useState("");
  const [resourceExistingFileUrl, setResourceExistingFileUrl] = useState("");
  const [resourceExistingIsImage, setResourceExistingIsImage] = useState(false);

  const videoFileRef = useRef(null);
  const videoThumbRef = useRef(null);
  const resourceFileRef = useRef(null);
  const [videoFile, setVideoFile] = useState(null);
  const [videoThumb, setVideoThumb] = useState(null);
  const [resourceFile, setResourceFile] = useState(null);
  const [resourceFieldError, setResourceFieldError] = useState("");

  const activeWeekSection = useMemo(() => {
    if (!activeWeekSectionId) return null;
    return weekSections.find((x) => String(x.id) === String(activeWeekSectionId)) || null;
  }, [activeWeekSectionId, weekSections]);

  /** Section being edited in the modal (stable id + current list row). */
  const editingWeekSection = useMemo(() => {
    if (!editingWeekSectionId) return null;
    return weekSections.find((x) => String(x.id) === String(editingWeekSectionId)) || null;
  }, [editingWeekSectionId, weekSections]);

  useEffect(() => {
    let cancelled = false;
    async function loadAll() {
      if (!courseId || !weekId) return;
      setLoading(true);
      setError("");
      try {
        const [courseRes, weekRes, sectionsRes] = await Promise.all([
          adminCourseGetAction(courseId),
          adminCourseWeekContentGetAction(weekId),
          adminCourseWeekSectionsListAction(weekId)
        ]);

        if (cancelled) return;
        if (courseRes?.success && courseRes?.data?.title) setCourseTitle(courseRes.data.title);
        if (!weekRes?.success) throw new Error(weekRes?.message || "Failed to load week.");

        setWeek({
          title: weekRes?.data?.title || "",
          subtitle: weekRes?.data?.subtitle || "",
          heading_title: weekRes?.data?.heading_title || "",
          description_title: weekRes?.data?.description_title || "",
          short_description: weekRes?.data?.short_description || "",
          description: weekRes?.data?.description || "",
          icon: weekRes?.data?.icon || "",
          week_image: weekRes?.data?.week_image || ""
        });

        const ws = Array.isArray(sectionsRes?.data) ? sectionsRes.data : [];
        setWeekSections(ws);
        const first = ws.length ? ws[0].id : null;
        setActiveWeekSectionId(first || null);
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.message || e?.message || "Failed to load week content.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    loadAll();
    return () => {
      cancelled = true;
    };
  }, [courseId, weekId]);

  // Scroll-spy: when a section card becomes active in viewport, auto-select it in left nav
  useEffect(() => {
    if (!weekSections || weekSections.length === 0) return undefined;
    let raf = 0;
    const onScroll = () => {
      if (raf) return;
      raf = window.requestAnimationFrame(() => {
        raf = 0;
        // Do not change selection while a modal is open — edit save keys off a pinned section id,
        // but left-nav highlight must stay stable too while editing.
        if (
          editSectionModalOpen ||
          addSectionModalOpen ||
          videoModalOpen ||
          resourceModalOpen ||
          videoPreviewOpen ||
          deleteModalOpen
        ) {
          return;
        }
        const cards = Array.from(document.querySelectorAll("[data-week-section-id]"));
        if (!cards.length) return;
        const scrollPos = window.scrollY + 120;
        for (let i = 0; i < cards.length; i += 1) {
          const el = cards[i];
          const top = el.offsetTop;
          const bottom = top + el.offsetHeight;
          if (top <= scrollPos && bottom > scrollPos) {
            const id = el.getAttribute("data-week-section-id");
            if (id && String(activeWeekSectionId) !== String(id)) setActiveWeekSectionId(id);
            break;
          }
        }
      });
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    // Run once after render
    setTimeout(() => onScroll(), 0);
    return () => {
      if (raf) window.cancelAnimationFrame(raf);
      window.removeEventListener("scroll", onScroll);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [weekSections, editSectionModalOpen, addSectionModalOpen, videoModalOpen, resourceModalOpen, videoPreviewOpen, deleteModalOpen]);

  // section editing now uses `editSectionForm` only (same fields as Add Section)

  useEffect(() => {
    let cancelled = false;
    async function loadAllMediaForWeek() {
      if (!weekSections || weekSections.length === 0) return;
      const ids = weekSections
        .filter((ws) => String(ws?.section_type || "").toLowerCase() !== "content")
        .map((ws) => String(ws.id));
      if (ids.length === 0) return;

      setLoadingMediaBySectionId((prev) => {
        const next = { ...prev };
        ids.forEach((id) => { next[id] = true; });
        return next;
      });

      try {
        const results = await Promise.all(ids.map((id) => adminSectionMediaListAction(id)));
        if (cancelled) return;
        const nextMedia = {};
        ids.forEach((id, idx) => {
          const r = results[idx];
          nextMedia[id] = (r?.success && Array.isArray(r.data)) ? r.data : [];
        });
        setMediaBySectionId((prev) => ({ ...prev, ...nextMedia }));
      } finally {
        if (!cancelled) {
          setLoadingMediaBySectionId((prev) => {
            const next = { ...prev };
            ids.forEach((id) => { next[id] = false; });
            return next;
          });
        }
      }
    }
    loadAllMediaForWeek();
    return () => { cancelled = true; };
  }, [weekSections]);

  const uploadWeekImageField = async (file, field, toastId, setUploading, inputRef) => {
    if (!file) return;
    if (!String(file.type || "").startsWith("image/")) {
      toast.error("Please choose an image file.", { toastId });
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("upload", file);
      const token = getAdminAuthToken();
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const resp = await axios.post(buildApiUrl("/admin/upload/image"), fd, {
        headers,
        withCredentials: true
      });
      const url = resp?.data?.url;
      if (!url) throw new Error("Upload failed");
      setWeek((p) => ({ ...p, [field]: url }));
      toast.success("Image uploaded. Click Save Week Settings to store it for this week.", { toastId });
    } catch (e) {
      toast.error(e?.response?.data?.message || e?.message || "Upload failed", { toastId });
    } finally {
      setUploading(false);
      if (inputRef?.current) inputRef.current.value = "";
    }
  };

  const uploadWeekIcon = (file) =>
    uploadWeekImageField(file, "icon", "week-icon", setUploadingIcon, iconInputRef);

  const uploadWeekImage = (file) =>
    uploadWeekImageField(file, "week_image", "week-image", setUploadingWeekImage, weekImageInputRef);

  const uploadSectionThumbnail = async (file, setForm, inputRef) => {
    if (!file) return;
    if (!String(file.type || "").startsWith("image/")) {
      toast.error("Please choose an image file.", { toastId: "section-thumb" });
      return;
    }
    setUploadingSectionThumb(true);
    try {
      const fd = new FormData();
      fd.append("upload", file);
      const token = getAdminAuthToken();
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const resp = await axios.post(buildApiUrl("/admin/upload/image"), fd, {
        headers,
        withCredentials: true
      });
      const url = resp?.data?.url;
      if (!url) throw new Error("Upload failed");
      setForm((p) => ({ ...p, thumbnail_url: url }));
      toast.success("Thumbnail uploaded. Save the section to keep it.", { toastId: "section-thumb" });
    } catch (e) {
      toast.error(e?.response?.data?.message || e?.message || "Upload failed", { toastId: "section-thumb" });
    } finally {
      setUploadingSectionThumb(false);
      if (inputRef?.current) inputRef.current.value = "";
    }
  };

  const validateWeekSettings = () => {
    const errs = {};
    const title = String(week.title || "").trim();
    if (!title) errs.title = "Week Title is required.";
    if (!editorPlainText(week.short_description)) errs.shortDescription = "Short Description is required.";
    if (!editorPlainText(week.description)) errs.description = "Description is required.";
    return errs;
  };

  const saveWeekSettings = async () => {
    setSavingWeek(true);
    setError("");
    try {
      const errs = validateWeekSettings();
      setWeekFieldErrors(errs);
      if (Object.keys(errs).length) return;

      const payload = {
        title: String(week.title || "").trim(),
        subtitle: String(week.subtitle || "").trim() || null,
        heading_title: String(week.heading_title || "").trim() || null,
        description_title: String(week.description_title || "").trim() || null,
        short_description: week.short_description || "",
        description: week.description || "",
        icon: String(week.icon || "").trim() || null,
        week_image: String(week.week_image || "").trim() || null
      };
      const res = await adminCourseWeekContentUpdateAction(weekId, payload);
      if (!res?.success) throw new Error(res?.message || "Save failed");
      toast.success("Week settings saved", { toastId: "week-settings" });
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || "Save failed");
      toast.error("Save failed", { toastId: "week-settings" });
    } finally {
      setSavingWeek(false);
    }
  };

  const isValidPdf = (file) => {
    if (!file) return false;
    const validMime = String(file.type || "").toLowerCase() === "application/pdf";
    const validExt = /\.pdf$/i.test(String(file.name || ""));
    return validMime || validExt;
  };

  const saveEditedSection = async () => {
    // Always use the section pinned when the edit modal opened — never activeWeekSection
    // (scroll-spy / nav can change that while the modal is open and update the wrong row).
    const ws = editingWeekSection;
    if (!ws?.id) {
      toast.error("Section id missing — reopen Edit Section and try again", { toastId: "edit-section" });
      return;
    }
    const definitionId = ws.section_id != null && ws.section_id !== "" ? Number(ws.section_id) : null;
    const title = String(editSectionForm.title || "").trim();
    if (!title) {
      toast.error("Section Title is required", { toastId: "edit-section" });
      return;
    }
    setSavingSection(true);
    try {
      const typeNorm = String(editSectionForm.type || "").toLowerCase();
      const type = normalizeAdminSectionType(typeNorm);
      const hasExistingPdf = String(editSectionForm.file_url || "").trim().length > 0;
      if (type === "content") {
        if (editSectionPdfFile && !isValidPdf(editSectionPdfFile)) {
          setEditSectionPdfError("Only PDF files are allowed.");
          toast.error("Only PDF files are allowed", { toastId: "edit-section-pdf" });
          return;
        }
        if (!hasExistingPdf && !editSectionPdfFile) {
          setEditSectionPdfError("PDF file is required for Content sections.");
          toast.error("PDF file is required for Content sections", { toastId: "edit-section-pdf" });
          return;
        }
      }
      const display_mode =
        type === "resources"
          ? "list"
          : type === "content"
            ? null
            : (ws?.section_display_mode || ws?.display_mode || null);

      // Update base week_sections record (type always; title only when it actually changed).
      // Title uniqueness is per week_id only — same title on other weeks is fine.
      // PUT /admin/week-sections/:id uses week_sections.id (= course_week_sections.section_id).
      if (definitionId && Number.isInteger(definitionId) && definitionId > 0) {
        const originalTitle = String(ws?.section_title || "").trim();
        const titleChanged = title.toLowerCase() !== originalTitle.toLowerCase();
        const payload = { type, display_mode };
        if (titleChanged) payload.title = title;
        // Backfill week_sections.week_id only when the definition row is missing it
        // (do not use cws.week_id — that is always set on the mapping row).
        const sectionWeekId = ws?.section_week_id;
        if (sectionWeekId == null || sectionWeekId === "") payload.week_id = weekId;
        const upd = await adminWeekSectionUpdateAction(definitionId, payload);
        if (!upd?.success) throw new Error(upd?.message || "Failed to update section title/type");
      }

      // Update mapping fields — PUT /admin/course-week-sections/:id uses course_week_sections.id
      const mappingPayload = new FormData();
      mappingPayload.append("audience", editSectionForm.audience || "Both");
      mappingPayload.append("content", editSectionForm.content || "");
      mappingPayload.append("thumbnail_url", String(editSectionForm.thumbnail_url || "").trim() || "");
      mappingPayload.append("is_core_videos", editSectionForm.is_core_videos ? "1" : "0");
      mappingPayload.append("is_complete_section", editSectionForm.is_complete_section ? "1" : "0");
      mappingPayload.append("video_type", type === "videos" && editSectionForm.video_type ? String(editSectionForm.video_type) : "");
      mappingPayload.append("visibility", String(editSectionForm.visibility || defaultVisibilityForType(type, "all")));
      mappingPayload.append("display_title", String(editSectionForm.display_title || "").trim());
      mappingPayload.append("display_subtitle", String(editSectionForm.display_subtitle || "").trim());
      if (type === "content") {
        if (editSectionPdfFile) mappingPayload.append("file_url", editSectionPdfFile);
        else mappingPayload.append("file_url", String(editSectionForm.file_url || "").trim() || "");
      } else {
        mappingPayload.append("file_url", "");
      }
      const res = await adminCourseWeekSectionUpdateFormDataAction(ws.id, mappingPayload);
      if (!res?.success) throw new Error(res?.message || "Save failed");

      toast.success("Section updated", { toastId: "edit-section" });
      setEditSectionModalOpen(false);
      setEditingWeekSectionId(null);

      const list = await adminCourseWeekSectionsListAction(weekId);
      setWeekSections(Array.isArray(list?.data) ? list.data : []);
      return;
    } catch (e) {
      toast.error(e?.response?.data?.message || e?.message || "Save failed", { toastId: "edit-section" });
    } finally {
      setSavingSection(false);
    }
  };

  const openAddSection = async () => {
    if (!weekId) return;
    setAddSectionModalOpen(true);
    setLoadingAvailableSections(true);
    setAvailableSections([]);
    setAllSections([]);
    setWeekSectionDatalistSections([]);
    setAddSectionForm(emptySectionForm());
    setAddSectionPdfFile(null);
    setAddSectionPdfError("");
    try {
      const [availableRes, allRes] = await Promise.all([
        adminWeekSectionsAvailableAction({ week_id: weekId }),
        adminWeekSectionsAvailableAction({ week_id: weekId, all_for_week: 1 })
      ]);
      if (!availableRes?.success) throw new Error(availableRes?.message || "Failed to load sections.");
      if (!allRes?.success) throw new Error(allRes?.message || "Failed to load sections.");
      setAvailableSections(Array.isArray(availableRes.data) ? availableRes.data : []);
      setAllSections(Array.isArray(allRes.data) ? allRes.data : []);
      setWeekSectionDatalistSections(Array.isArray(allRes.data) ? allRes.data : []);
    } catch (e) {
      toast.error(e?.response?.data?.message || e?.message || "Failed to load sections", { toastId: "add-section" });
      setAvailableSections([]);
      setAllSections([]);
      setWeekSectionDatalistSections([]);
    } finally {
      setLoadingAvailableSections(false);
    }
  };

  const openEditSection = async (id) => {
    if (!id) return;
    setWeekSectionDatalistSections([]);
    if (weekId) {
      try {
        const res = await adminWeekSectionsAvailableAction({ week_id: weekId, all_for_week: 1 });
        if (res?.success) setWeekSectionDatalistSections(Array.isArray(res.data) ? res.data : []);
      } catch (e) {
        toast.error(e?.response?.data?.message || e?.message || "Failed to load section titles", { toastId: "edit-section-load" });
      }
    }
    setActiveWeekSectionId(id);
    setEditingWeekSectionId(id);
    const ws = weekSections.find((x) => String(x.id) === String(id));
    if (ws) {
      setEditSectionForm({
        title: ws?.section_title || "",
        audience: ws?.audience || "Both",
        type: normalizeAdminSectionType(ws?.section_type || ws?.type),
        content: ws?.content || "",
        file_url: ws?.file_url || "",
        thumbnail_url: ws?.thumbnail_url || "",
        is_core_videos: ws?.is_core_videos === 1 || ws?.is_core_videos === true,
        is_complete_section: ws?.is_complete_section === 1 || ws?.is_complete_section === true,
        video_type: ws?.video_type != null && ws?.video_type !== "" ? String(ws.video_type) : "",
        visibility: String(ws?.visibility || "all").toLowerCase(),
        display_title: ws?.display_title || "",
        display_subtitle: ws?.display_subtitle || ""
      });
      setEditSectionPdfFile(null);
      setEditSectionPdfError("");
    } else {
      setEditSectionForm(emptySectionForm());
      setEditSectionPdfFile(null);
      setEditSectionPdfError("");
    }
    setEditSectionModalOpen(true);
  };

  const closeAnyModal = () => {
    setVideoPreviewOpen(false);
    setVideoModalOpen(false);
    setResourceModalOpen(false);
    setDeleteModalOpen(false);
    setVideoEditingId(null);
    videoEditSourceRef.current = null;
    setResourceEditingId(null);
    setResourceFieldError("");
    setDeleteTarget({ kind: "", id: null, name: "" });
  };

  useEffect(() => {
    const anyOpen = videoPreviewOpen || videoModalOpen || resourceModalOpen || deleteModalOpen || addSectionModalOpen || editSectionModalOpen;
    document.body.style.overflow = anyOpen ? "hidden" : "";
    const onKeyDown = (e) => {
      if (e.key === "Escape") closeAnyModal();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.body.style.overflow = "";
      document.removeEventListener("keydown", onKeyDown);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videoPreviewOpen, videoModalOpen, resourceModalOpen, deleteModalOpen, addSectionModalOpen, editSectionModalOpen]);

  const openVideoPreviewModal = (ws, m) => {
    const sectionName = ws?.section_title || "Section";
    setVideoPreview({
      title: m?.title || "Video",
      description: m?.description || "",
      duration: m?.duration || "0:00",
      section: sectionName,
      uploadedLabel: m?.created_at ? formatDateShort(m.created_at) : "",
      url: m?.file_url ? resolvePublicUrl(m.file_url) : ""
    });
    setVideoPreviewOpen(true);
  };

  const openAddVideoModal = (ws) => {
    setCurrentMediaSectionId(ws?.id || null);
    setVideoEditingId(null);
    videoEditSourceRef.current = null;
    setVideoForm({ title: "", type: "2", description: "", duration: "", position: "", video_orientation: "landscape", provider: "Selfhosted", file_url: "", mediaGroup: defaultVideoMediaGroup(ws) });
    setVideoFile(null);
    setVideoThumb(null);
    setVideoExistingFileUrl("");
    setVideoExistingThumbUrl("");
    setVideoModalOpen(true);
  };

  const openEditVideoModal = (ws, m) => {
    if (!m?.id) return;
    setCurrentMediaSectionId(ws?.id || null);
    setVideoEditingId(m.id);
    videoEditSourceRef.current = m;
    const prov = sectionMediaProviderFromRow(m);
    const rawUrl = m?.file_url != null ? String(m.file_url) : "";
    setVideoForm({
      title: m?.title || "",
      type: videoTypeFormValue(m?.video_type),
      description: m?.description || "",
      duration: m?.duration || "",
      position: sectionMediaPositionFormValueFromRow(m),
      video_orientation: videoOrientationFormValue(m?.video_orientation),
      provider: prov,
      file_url: prov === "Thirdparty" ? rawUrl : "",
      mediaGroup: String(m?.media_group || "").trim().toLowerCase()
    });
    setVideoFile(null);
    setVideoThumb(null);
    setVideoExistingFileUrl(m?.file_url ? resolvePublicUrl(m.file_url) : "");
    setVideoExistingThumbUrl(m?.thumbnail_url ? resolvePublicUrl(m.thumbnail_url) : "");
    setVideoModalOpen(true);
  };

  const openAddResourceModal = (ws) => {
    setCurrentMediaSectionId(ws?.id || null);
    setResourceEditingId(null);
    setResourceForm({ name: "", fileType: "PDF", status: "Active", description: "", position: "", isEditable: false, readingType: "priority", mediaGroup: "" });
    setResourceFile(null);
    setResourceFieldError("");
    setResourceExistingFileUrl("");
    setResourceExistingIsImage(false);
    setResourceModalOpen(true);
  };

  const openEditSectionMedia = (ws, m) => {
    if (isVideoMedia(m)) openEditVideoModal(ws, m);
    else openEditResourceModal(ws, m);
  };

  const openEditResourceModal = (ws, m) => {
    if (!m?.id) return;
    setCurrentMediaSectionId(ws?.id || null);
    setResourceEditingId(m.id);
    const statusRaw = String(m?.status || "active").toLowerCase();
    const status =
      statusRaw === "completed" ? "Completed" : statusRaw === "draft" ? "Draft" : "Active";
    const url = m?.file_url ? resolvePublicUrl(m.file_url) : "";
    const ft = String(m?.file_type || "").toLowerCase();
    const isImg = ft.includes("image") || /\.(png|jpg|jpeg|webp|gif)(\?|$)/i.test(url);
    setResourceForm({
      name: m?.title || "",
      fileType: m?.file_type || "PDF",
      status,
      description: m?.description || "",
      position: sectionMediaPositionFormValueFromRow(m),
      isEditable: m?.is_editable === 1 || m?.is_editable === true || m?.is_editable === "1",
      readingType: String(m?.reading_type || "priority").toLowerCase() === "optional" ? "optional" : "priority",
      mediaGroup: String(m?.media_group || "").trim().toLowerCase()
    });
    setResourceFile(null);
    setResourceFieldError("");
    setResourceExistingFileUrl(url);
    setResourceExistingIsImage(!!isImg);
    setResourceModalOpen(true);
  };

  const openDeleteModal = (kind, m) => {
    setDeleteTarget({ kind, id: m?.id || null, name: m?.title || "Item" });
    setDeleteModalOpen(true);
  };

  const saveNewSection = async () => {
    const typed = String(addSectionForm.title || "").trim();
    if (!typed) {
      toast.error("Section Title is required", { toastId: "add-section" });
      return;
    }

    const alreadyAdded = weekSections.some((ws) => String(ws?.section_title || "").trim().toLowerCase() === typed.toLowerCase());
    if (alreadyAdded) {
      toast.error("This section is already added.", { toastId: "add-section" });
      return;
    }

    const matchAll = allSections.find((s) => String(s?.title || "").trim().toLowerCase() === typed.toLowerCase());
    let sectionId = matchAll?.id ? Number(matchAll.id) : null;
    const audience = addSectionForm.audience || "Both";
    const typeNorm = String(addSectionForm.type || "").toLowerCase();
    const type = normalizeAdminSectionType(typeNorm);
    const hasExistingPdf = String(addSectionForm.file_url || "").trim().length > 0;
    if (type === "content") {
      if (addSectionPdfFile && !isValidPdf(addSectionPdfFile)) {
        setAddSectionPdfError("Only PDF files are allowed.");
        toast.error("Only PDF files are allowed", { toastId: "add-section-pdf" });
        return;
      }
      if (!hasExistingPdf && !addSectionPdfFile) {
        setAddSectionPdfError("PDF file is required for Content sections.");
        toast.error("PDF file is required for Content sections", { toastId: "add-section-pdf" });
        return;
      }
    }
    const initialContent = addSectionForm.content || "";
    const display_mode = type === "resources" ? "list" : null;
    try {
      if (!sectionId) {
        const created = await adminWeekSectionCreateAction({
          week_id: weekId,
          title: typed,
          type,
          display_mode
        });
        if (!created?.success) throw new Error(created?.message || created?.msg || "Create section title failed");
        sectionId = created?.data?.id ? Number(created.data.id) : null;
      }
      if (!sectionId) throw new Error("Section id missing");

      const payload = new FormData();
      payload.append("section_id", String(sectionId));
      payload.append("audience", audience);
      payload.append("active", "1");
      payload.append("content", initialContent);
      payload.append("thumbnail_url", String(addSectionForm.thumbnail_url || "").trim() || "");
      payload.append("is_core_videos", addSectionForm.is_core_videos ? "1" : "0");
      payload.append("is_complete_section", addSectionForm.is_complete_section ? "1" : "0");
      payload.append("video_type", type === "videos" && addSectionForm.video_type ? String(addSectionForm.video_type) : "");
      payload.append("visibility", String(addSectionForm.visibility || defaultVisibilityForType(type, "all")));
      payload.append("display_title", String(addSectionForm.display_title || "").trim());
      payload.append("display_subtitle", String(addSectionForm.display_subtitle || "").trim());
      if (type === "content") {
        if (addSectionPdfFile) payload.append("file_url", addSectionPdfFile);
        else payload.append("file_url", String(addSectionForm.file_url || "").trim() || "");
      } else {
        payload.append("file_url", "");
      }
      const res = await adminCourseWeekSectionCreateFormDataAction(weekId, payload);
      if (!res?.success) throw new Error(res?.message || "Create failed");
      toast.success("Section added", { toastId: "add-section" });
      setAddSectionModalOpen(false);
      const list = await adminCourseWeekSectionsListAction(weekId);
      setWeekSections(Array.isArray(list?.data) ? list.data : []);
      if (res?.data?.id) setActiveWeekSectionId(res.data.id);
    } catch (e) {
      toast.error(e?.response?.data?.message || e?.message || "Create failed", { toastId: "add-section" });
    }
  };

  const saveVideo = async () => {
    const wsId = currentMediaSectionId || activeWeekSection?.id;
    if (!wsId) return;
    const title = String(videoForm.title || "").trim();
    if (!title) {
      toast.error("Please enter a video title", { toastId: "video-save" });
      return;
    }
    const provider = String(videoForm.provider || "Selfhosted") === "Thirdparty" ? "Thirdparty" : "Selfhosted";
    const externalUrl = String(videoForm.file_url || "").trim();
    const origIsThirdParty =
      !!videoEditSourceRef.current &&
      String(videoEditSourceRef.current.provider || "").toLowerCase() === "thirdparty";

    if (provider === "Thirdparty") {
      if (!externalUrl) {
        toast.error("Please enter a video URL", { toastId: "video-save" });
        return;
      }
    } else {
      const canOmitFile = !!videoEditingId && !!videoEditSourceRef.current && !origIsThirdParty;
      if (!videoFile && !canOmitFile) {
        toast.error("Please upload a video file", { toastId: "video-save" });
        return;
      }
    }

    const fd = new FormData();
    fd.append("title", title);
    fd.append("provider", provider);
    fd.append("type", "video");
    fd.append("file_type", "video");
    fd.append("media_kind", "video");
    fd.append("description", String(videoForm.description || ""));
    fd.append("duration", String(videoForm.duration || ""));
    if (videoForm.type) fd.append("video_type", String(videoForm.type));
    fd.append("video_orientation", videoOrientationFormValue(videoForm.video_orientation));
    fd.append("position", String(sectionMediaPositionIntForSave(videoForm.position)));
    fd.append("media_group", String(videoForm.mediaGroup || ""));
    if (provider === "Thirdparty") fd.append("file_url", externalUrl);
    if (provider === "Selfhosted" && videoFile) fd.append("file", videoFile);
    if (videoThumb) fd.append("thumbnail", videoThumb);

    try {
      const res = videoEditingId
        ? await adminSectionMediaUpdateAction(videoEditingId, fd)
        : await adminSectionMediaCreateAction(wsId, fd);
      if (!res?.success) throw new Error(res?.message || "Save failed");
      toast.success(videoEditingId ? `Video "${title}" updated successfully` : `Video "${title}" added successfully`, { toastId: "video-save" });
      setVideoModalOpen(false);
      setVideoEditingId(null);
      videoEditSourceRef.current = null;
      setVideoFile(null);
      setVideoThumb(null);
      const list = await adminSectionMediaListAction(wsId);
      if (list?.success) setMediaBySectionId((p) => ({ ...p, [String(wsId)]: Array.isArray(list.data) ? list.data : [] }));
      const sectionsRes = await adminCourseWeekSectionsListAction(weekId);
      if (sectionsRes?.success) setWeekSections(Array.isArray(sectionsRes.data) ? sectionsRes.data : []);
    } catch (e) {
      toast.error(e?.response?.data?.message || e?.message || "Save failed", { toastId: "video-save" });
    }
  };

  const saveResource = async () => {
    const wsId = currentMediaSectionId || activeWeekSection?.id;
    if (!wsId) return;
    const name = String(resourceForm.name || "").trim();
    if (!name) {
      toast.error("Please enter a resource name", { toastId: "resource-save" });
      return;
    }
    if (!resourceEditingId && !resourceFile) {
      toast.error("Please upload a file", { toastId: "resource-save" });
      return;
    }

    const status = String(resourceForm.status || "Active").toLowerCase();
    const fd = new FormData();
    fd.append("title", name);
    fd.append("provider", "Selfhosted");
    fd.append("description", String(resourceForm.description || ""));
    fd.append("file_type", String(resourceForm.fileType || "PDF"));
    fd.append("is_editable", String(resourceForm.fileType === "PDF" && resourceForm.isEditable ? 1 : 0));
    fd.append("status", status === "completed" ? "completed" : status === "draft" ? "draft" : "active");
    const typeHint = String(resourceForm.fileType || "").toLowerCase().includes("image") ? "image" : "document";
    fd.append("type", typeHint);
    fd.append("media_kind", "resource");
    fd.append("position", String(sectionMediaPositionIntForSave(resourceForm.position)));
    fd.append("reading_type", String(resourceForm.readingType || "priority").toLowerCase() === "optional" ? "optional" : "priority");
    fd.append("media_group", String(resourceForm.mediaGroup || ""));
    if (resourceFile) fd.append("file", resourceFile);

    try {
      const res = resourceEditingId
        ? await adminSectionMediaUpdateAction(resourceEditingId, fd)
        : await adminSectionMediaCreateAction(wsId, fd);
      if (!res?.success) throw new Error(res?.message || "Save failed");
      toast.success(resourceEditingId ? `Resource "${name}" updated successfully` : `Resource "${name}" added successfully`, { toastId: "resource-save" });
      setResourceModalOpen(false);
      setResourceEditingId(null);
      setResourceFile(null);
      setResourceFieldError("");
      const list = await adminSectionMediaListAction(wsId);
      if (list?.success) setMediaBySectionId((p) => ({ ...p, [String(wsId)]: Array.isArray(list.data) ? list.data : [] }));
      const sectionsRes = await adminCourseWeekSectionsListAction(weekId);
      if (sectionsRes?.success) setWeekSections(Array.isArray(sectionsRes.data) ? sectionsRes.data : []);
    } catch (e) {
      toast.error(e?.response?.data?.message || e?.message || "Save failed", { toastId: "resource-save" });
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget?.id) return;
    const wsId = currentMediaSectionId || activeWeekSection?.id;
    try {
      const res = await adminSectionMediaDeleteAction(deleteTarget.id);
      if (!res?.success) throw new Error(res?.message || "Delete failed");
      toast.success(`${deleteTarget.kind === "resource" ? "Resource" : "Video"} deleted successfully`, { toastId: "delete" });
      setDeleteModalOpen(false);
      if (wsId) {
        const list = await adminSectionMediaListAction(wsId);
        if (list?.success) setMediaBySectionId((p) => ({ ...p, [String(wsId)]: Array.isArray(list.data) ? list.data : [] }));
      }
      const sectionsRes = await adminCourseWeekSectionsListAction(weekId);
      if (sectionsRes?.success) setWeekSections(Array.isArray(sectionsRes.data) ? sectionsRes.data : []);
    } catch (e) {
      toast.error("Delete failed", { toastId: "delete" });
    }
  };

  const sectionNavRows = useMemo(() => {
    return (weekSections || []).map((ws) => {
      const title = ws?.section_title || `Section ${ws?.section_id || ws?.id || ""}`.trim();
      const audience = audienceKeyShort(ws?.audience);
      const rawType = String(ws?.section_type || ws?.type || "").toLowerCase();
      const titleLower = String(title || "").toLowerCase();
      const sectionType = isHubSectionType(rawType)
        ? rawType
        : rawType === "content"
          ? "content"
          : rawType === "resources" || rawType === "resource" || titleLower.includes("resources")
            ? "resources"
            : "videos";
      const mediaCount = Number(ws?.media_count || 0);
      const hasContent = editorPlainText(ws?.content).length > 0;
      let status = "";
      let badge = "badge-info";
      if (sectionType === "content") {
        status = hasContent ? "Added" : "Not added";
        badge = hasContent ? "badge-success" : "badge-info";
      } else if (sectionType === "resources" || isHubSectionType(sectionType)) {
        status = `${mediaCount} item${mediaCount === 1 ? "" : "s"}`;
        badge = mediaCount > 0 ? "badge-info" : "badge-danger";
      } else {
        status = `${mediaCount} video${mediaCount === 1 ? "" : "s"}`;
        badge = mediaCount > 0 ? "badge-success" : "badge-danger";
      }
      return {
        id: ws?.id,
        title,
        audience,
        badge,
        status,
        section_type: sectionType
      };
    });
  }, [weekSections]);

  return (
    <div className="content">
      {error ? (
        <div className="banner banner-warning">
          <span className="banner-icon">⚠️</span>
          <div className="banner-text">
            <strong>Week content unavailable.</strong> {error}
          </div>
        </div>
      ) : null}

      <div className="breadcrumb">
        <Link to="/courses">Courses</Link>
        <span>›</span>
        <Link to={`/courses/${courseId}/edit`}>{courseTitle}</Link>
        <span>›</span>
        <Link to={`/courses/${courseId}/weeks`}>Weeks</Link>
        <span>›</span>
        <span>{week.title || `Week ${weekId}`}</span>
      </div>

        <div className="page-banner">
        <div className="page-banner-content">
          <div className="page-banner-icon blue" style={{ overflow: "hidden", padding: 0 }}>
            {week.icon ? (
              <img
                src={resolvePublicUrl(week.icon)}
                alt=""
                style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
              />
            ) : (
              String(week.title || "WK").slice(0, 2).toUpperCase()
            )}
          </div>
          <div className="page-banner-text">
            <h2>{week.title || "Week Content"}</h2>
            <p>Course: {courseTitle} · Week ID: {weekId}</p>
          </div>
        </div>
        <div className="flex gap-3">
          <Link to={`/courses/${courseId}/weeks`} className="btn btn-outline btn-sm">
            ← Back
          </Link>
          <button className="btn btn-primary" type="button" onClick={saveWeekSettings} disabled={savingWeek || loading}>
            {savingWeek ? "Saving…" : "Save Week Settings"}
          </button>
        </div>
      </div>

      {loading ? (
        <div className="empty-state" style={{ padding: "28px 12px" }}>
          Loading…
        </div>
      ) : (
        <>
          <div className="card mb-6">
            <h2 className="section-title">Week Settings</h2>
            <div className="form-row">
              <div className="form-group">
                <label>
                  Week Title <span className="text-danger">*</span>
                </label>
                <input
                  className="form-control"
                  value={week.title}
                  onChange={(e) => {
                    setWeek((p) => ({ ...p, title: e.target.value }));
                    setWeekFieldErrors((p) => ({ ...p, title: "" }));
                  }}
                  aria-invalid={!!weekFieldErrors.title}
                  style={weekFieldErrors.title ? { borderColor: "var(--danger)" } : undefined}
                />
                {weekFieldErrors.title ? (
                  <div className="small-label" style={{ marginTop: 6, color: "var(--danger)" }}>
                    {weekFieldErrors.title}
                  </div>
                ) : null}
              </div>
              <div className="form-group">
                <label>Week Subtitle (optional)</label>
                <input
                  className="form-control"
                  value={week.subtitle}
                  onChange={(e) => setWeek((p) => ({ ...p, subtitle: e.target.value }))}
                />
              </div>
            </div>

            <div className="form-group">
              <label>Heading Title</label>
              <input
                type="text"
                className="form-control"
                placeholder="Optional heading title..."
                value={week.heading_title || ""}
                onChange={(e) =>
                  setWeek((p) => ({ ...p, heading_title: e.target.value }))
                }
              />
            </div>

            <div className="form-group">
              <label>Description title</label>
              <input
                type="text"
                className="form-control"
                placeholder="Heading shown above the week description (optional)"
                value={week.description_title || ""}
                onChange={(e) =>
                  setWeek((p) => ({ ...p, description_title: e.target.value }))
                }
              />
            </div>

            <div className="form-group">
              <label>Week icon</label>
              <p className="small-label" style={{ marginBottom: 8 }}>
                Small image shown in the week banner. Save Week Settings after uploading.
              </p>
              <input
                ref={iconInputRef}
                type="file"
                accept="image/*"
                style={{ display: "none" }}
                onChange={(e) => {
                  const file = e.target.files && e.target.files[0] ? e.target.files[0] : null;
                  if (file) uploadWeekIcon(file);
                }}
              />
              <div className="flex gap-3 items-center" style={{ flexWrap: "wrap" }}>
                <button
                  type="button"
                  className="btn btn-outline btn-sm"
                  disabled={uploadingIcon || loading}
                  onClick={() => iconInputRef.current && iconInputRef.current.click()}
                >
                  {uploadingIcon ? "Uploading…" : week.icon ? "Change icon" : "Upload icon"}
                </button>
                {week.icon ? (
                  <button
                    type="button"
                    className="btn btn-outline btn-sm"
                    disabled={uploadingIcon || loading}
                    onClick={() => setWeek((p) => ({ ...p, icon: "" }))}
                  >
                    Remove
                  </button>
                ) : null}
                {week.icon ? (
                  <img
                    src={resolvePublicUrl(week.icon)}
                    alt="Week icon preview"
                    style={{ width: 56, height: 56, objectFit: "cover", borderRadius: 8, border: "1px solid var(--gray-300)" }}
                  />
                ) : null}
              </div>
            </div>

            <div className="form-group">
              <label>Week image</label>
              <p className="small-label" style={{ marginBottom: 8 }}>
                Larger image used for this week in the app. Save Week Settings after uploading.
              </p>
              <input
                ref={weekImageInputRef}
                type="file"
                accept="image/*"
                style={{ display: "none" }}
                onChange={(e) => {
                  const file = e.target.files && e.target.files[0] ? e.target.files[0] : null;
                  if (file) uploadWeekImage(file);
                }}
              />
              <div className="flex gap-3 items-center" style={{ flexWrap: "wrap" }}>
                <button
                  type="button"
                  className="btn btn-outline btn-sm"
                  disabled={uploadingWeekImage || loading}
                  onClick={() => weekImageInputRef.current && weekImageInputRef.current.click()}
                >
                  {uploadingWeekImage ? "Uploading…" : week.week_image ? "Change image" : "Upload image"}
                </button>
                {week.week_image ? (
                  <button
                    type="button"
                    className="btn btn-outline btn-sm"
                    disabled={uploadingWeekImage || loading}
                    onClick={() => setWeek((p) => ({ ...p, week_image: "" }))}
                  >
                    Remove
                  </button>
                ) : null}
                {week.week_image ? (
                  <img
                    src={resolvePublicUrl(week.week_image)}
                    alt="Week image preview"
                    style={{ width: 96, height: 96, objectFit: "cover", borderRadius: 8, border: "1px solid var(--gray-300)" }}
                  />
                ) : null}
              </div>
            </div>

            <div className="form-group">
              <label>
                Short Description <span className="text-danger">*</span>
              </label>
              <CKEditorWithSource
                heightClassName="ckeditor-height-200"
                containerStyle={
                  weekFieldErrors.shortDescription
                    ? { border: "1px solid var(--danger)", borderRadius: 6, overflow: "hidden" }
                    : { border: "1px solid var(--gray-300)", borderRadius: 6, overflow: "hidden" }
                }
                config={{ extraPlugins: [CkUploadAdapterPlugin] }}
                data={week.short_description}
                onChange={(html) => {
                  setWeek((p) => ({ ...p, short_description: html }));
                  setWeekFieldErrors((p) => ({ ...p, shortDescription: "" }));
                }}
              />
              {weekFieldErrors.shortDescription ? (
                <div className="small-label" style={{ marginTop: 6, color: "var(--danger)" }}>
                  {weekFieldErrors.shortDescription}
                </div>
              ) : null}
            </div>

            <div className="form-group mb-0">
              <label>
                Description <span className="text-danger">*</span>
              </label>
              <CKEditorWithSource
                heightClassName="ckeditor-height-400"
                containerStyle={
                  weekFieldErrors.description
                    ? { border: "1px solid var(--danger)", borderRadius: 6, overflow: "hidden" }
                    : { border: "1px solid var(--gray-300)", borderRadius: 6, overflow: "hidden" }
                }
                config={{ extraPlugins: [CkUploadAdapterPlugin] }}
                data={week.description}
                onChange={(html) => {
                  setWeek((p) => ({ ...p, description: html }));
                  setWeekFieldErrors((p) => ({ ...p, description: "" }));
                }}
              />
              {weekFieldErrors.description ? (
                <div className="small-label" style={{ marginTop: 6, color: "var(--danger)" }}>
                  {weekFieldErrors.description}
                </div>
              ) : null}
            </div>
          </div>

          <div className="content-section-grid">
            <div className="sticky-nav">
              <div className="flex items-center justify-between mb-3" style={{ gap: 10 }}>
                <h3 className="section-label" style={{ marginBottom: 0 }}>
                  Week Sections
                </h3>
                <button className="btn btn-primary btn-sm" type="button" onClick={openAddSection} disabled={loading || loadingAvailableSections}>
                  + Add Section
                </button>
              </div>
              <div className="section-nav">
                {sectionNavRows.length ? sectionNavRows.map((s, idx) => (
                  <div
                    key={s.id}
                    className={`section-nav-item ${String(activeWeekSectionId) === String(s.id) ? "active" : ""}`}
                    role="button"
                    tabIndex={0}
                    onClick={() => {
                      setActiveWeekSectionId(s.id);
                      const el = document.querySelector(`[data-week-section-id="${String(s.id)}"]`);
                      if (el && el.scrollIntoView) el.scrollIntoView({ behavior: "smooth", block: "start" });
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        setActiveWeekSectionId(s.id);
                        const el = document.querySelector(`[data-week-section-id="${String(s.id)}"]`);
                        if (el && el.scrollIntoView) el.scrollIntoView({ behavior: "smooth", block: "start" });
                      }
                    }}
                    style={{ textAlign: "left" }}
                  >
                    <span className="nav-num">{idx + 1}</span>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        openEditSection(s.id);
                      }}
                      aria-label="Edit section"
                      title="Edit section"
                      style={{
                        border: "1px solid var(--gray-200)",
                        background: "var(--white)",
                        borderRadius: 6,
                        padding: "6px 8px",
                        cursor: "pointer",
                        lineHeight: 0
                      }}
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path
                          d="M12 20H21"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5Z"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </button>
                    <span className="nav-label">{s.title}</span>
                    <span className="nav-audience">{s.audience}</span>
                    <span className="nav-status">
                      <span className={`badge ${s.badge} badge-sm`}>{s.status}</span>
                    </span>
                  </div>
                )) : (
                  <div className="empty-state" style={{ padding: "16px 10px" }}>
                    <div className="empty-icon">🫥</div>
                    No sections yet. Click <strong>+ Add Section</strong>.
                  </div>
                )}
              </div>
            </div>

            <div>
              {!weekSections?.length ? (
                <div className="empty-state" style={{ padding: "22px 12px", marginBottom: 24 }}>
                  <div className="empty-icon">🫥</div>
                  No sections yet. Click <strong>+ Add Section</strong>.
                </div>
              ) : (
                weekSections.map((ws, idx) => {
                  const wsId = String(ws?.id || "");
                  const sectionType = String(ws?.section_type || ws?.type || "").toLowerCase();
                  const displayMode = String(ws?.section_display_mode || ws?.display_mode || "").toLowerCase();
                  const items = mediaBySectionId[wsId] || [];
                  const isLoading = !!loadingMediaBySectionId[wsId];
                  return (
                    <div
                      key={wsId || idx}
                      className="section-card"
                      id={`section-${idx + 1}`}
                      data-week-section-id={wsId}
                      style={{ marginBottom: 24 }}
                    >
                      <div className="section-card-header">
                        <div className="section-card-title" style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                          {ws?.thumbnail_url ? (
                            <img
                              src={resolvePublicUrl(ws.thumbnail_url)}
                              alt=""
                              style={{
                                width: 44,
                                height: 44,
                                objectFit: "cover",
                                borderRadius: 10,
                                flexShrink: 0,
                                border: "1px solid var(--gray-200)"
                              }}
                            />
                          ) : null}
                          <span className={`section-num ${sectionColorClass(ws?.section_title)}`}>{idx + 1}</span>
                          <h3 style={{ margin: 0 }}>
                            {ws?.section_title || "Section"} {audienceKeyShort(ws?.audience)}
                          </h3>
                          {ws?.is_core_videos === 1 || ws?.is_core_videos === true ? (
                            <span className="badge badge-info badge-sm"> Core videos</span>
                          ) : null}
                          {String(ws?.visibility || "all").toLowerCase() === "preview" ? (
                            <span className="badge badge-warning badge-sm">Preview (149/150)</span>
                          ) : String(ws?.visibility || "all").toLowerCase() === "live" ? (
                            <span className="badge badge-info badge-sm">Live only</span>
                          ) : null}
                        </div>

                        {sectionType === "content" ? null : isHubSectionType(sectionType) ? (
                          <div className="flex gap-2">
                            <button
                              className="btn btn-primary btn-sm"
                              type="button"
                              onClick={() => { setActiveWeekSectionId(ws.id); openAddVideoModal(ws); }}
                            >
                              + Add Video
                            </button>
                            <button
                              className="btn btn-outline btn-sm"
                              type="button"
                              onClick={() => { setActiveWeekSectionId(ws.id); openAddResourceModal(ws); }}
                            >
                              + Add Resource
                            </button>
                          </div>
                        ) : (
                          <button
                            className="btn btn-primary btn-sm"
                            type="button"
                            onClick={() => {
                              setActiveWeekSectionId(ws.id);
                              if (sectionType === "resources") openAddResourceModal(ws);
                              else openAddVideoModal(ws);
                            }}
                          >
                            {sectionType === "resources"
                              ? "+ Add Resource"
                              : displayMode === "grid"
                                ? "+ Add Intro Video"
                                : displayMode === "list"
                                  ? "+ Add Core Video"
                                  : "+ Upload Video"}
                          </button>
                        )}
                      </div>

                      <div className="section-card-body">
                        {sectionType === "content" ? (
                          <>
                            <p className="help-text">
                              Textual information and detailed instructions for this section. Edit using the <strong>pencil</strong> icon on the left.
                            </p>
                            <div className="richtext-placeholder">
                              <div className="richtext-body medium">
                                <div dangerouslySetInnerHTML={{ __html: ws?.content || "<p class='help-text'>No content yet.</p>" }} />
                              </div>
                            </div>
                          </>
                        ) : isLoading ? (
                          <div className="empty-state" style={{ padding: "22px 12px" }}>
                            Loading…
                          </div>
                        ) : isHubSectionType(sectionType) ? (
                          <>
                            {editorPlainText(ws?.content) ? (
                              <div className="richtext-placeholder" style={{ marginBottom: 16 }}>
                                <div className="richtext-body medium">
                                  <div dangerouslySetInnerHTML={{ __html: ws?.content || "" }} />
                                </div>
                              </div>
                            ) : null}
                            <p className="help-text">
                              Hub page: one hero video plus grouped PDFs. Set <strong>media group</strong> on each item so preview users (149/150) see hub buttons. Live users never see this section if visibility is Preview.
                            </p>
                            {(items.filter(isVideoMedia) || []).map((m) => (
                              <div className="video-item" key={m.id} style={{ marginBottom: 10 }}>
                                <div className="video-thumb clickable" onClick={() => { setActiveWeekSectionId(ws.id); openVideoPreviewModal(ws, m); }}>
                                  ▶️
                                </div>
                                <div className="video-info">
                                  <h4>{m.title || "Video"}</h4>
                                  <p>Duration: {m.duration || "—"} {m.created_at ? `· Uploaded ${formatDateShort(m.created_at)}` : ""}</p>
                                  {m.media_group ? <p className="mt-2"><span className="badge badge-info badge-sm">{mediaGroupLabel(m.media_group)}</span></p> : null}
                                </div>
                                <div className="flex gap-2">
                                  <button className="btn btn-outline btn-sm" type="button" onClick={() => { setActiveWeekSectionId(ws.id); openEditSectionMedia(ws, m); }}>
                                    ✏️ Edit
                                  </button>
                                  <button className="btn btn-outline btn-sm text-danger" type="button" onClick={() => { setActiveWeekSectionId(ws.id); setCurrentMediaSectionId(ws.id); openDeleteModal(isVideoMedia(m) ? "video" : "resource", m); }}>
                                    🗑️
                                  </button>
                                </div>
                              </div>
                            ))}
                            <div id={`hub-resources-list-${wsId}`}>
                              {items.filter((m) => !isVideoMedia(m)).map((m, rIdx) => (
                                <div className="resource-item" key={m.id}>
                                  <span className="drag-handle-icon">⠿</span>
                                  <span className="resource-num">{rIdx + 1}</span>
                                  <div className="resource-info">
                                    <h4>{m.title || "—"}</h4>
                                    <p>
                                      {(m.file_type || "File").toString()}{" "}
                                      {m.size ? `· ${formatBytes(m.size)}` : ""}{" "}
                                      {m.created_at ? `· Uploaded ${formatDateShort(m.created_at)}` : ""}
                                    </p>
                                  </div>
                                  {m.media_group ? (
                                    <span className="badge badge-info badge-sm">{mediaGroupLabel(m.media_group)}</span>
                                  ) : (
                                    <span className={`badge ${String(m.reading_type || "priority").toLowerCase() === "optional" ? "badge-warning" : "badge-info"} badge-sm`}>
                                      {String(m.reading_type || "priority").toLowerCase() === "optional" ? "Optional" : "Priority"}
                                    </span>
                                  )}
                                  <div className="flex gap-2">
                                    <button className="btn btn-outline btn-sm" type="button" onClick={() => { setActiveWeekSectionId(ws.id); openEditResourceModal(ws, m); }}>
                                      ✏️
                                    </button>
                                    <button className="btn btn-outline btn-sm text-danger" type="button" onClick={() => { setActiveWeekSectionId(ws.id); setCurrentMediaSectionId(ws.id); openDeleteModal("resource", m); }}>
                                      🗑️
                                    </button>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </>
                        ) : sectionType === "resources" ? (
                          <>
                            {editorPlainText(ws?.content) ? (
                              <div className="richtext-placeholder" style={{ marginBottom: 16 }}>
                                <div className="richtext-body medium">
                                  <div dangerouslySetInnerHTML={{ __html: ws?.content || "" }} />
                                </div>
                              </div>
                            ) : null}
                            <p className="help-text">
                              Downloadable resources — guides, activities, charts. These appear as a numbered list in the app.
                            </p>

                            <div id={`resources-list-${wsId}`}>
                              {items.map((m, rIdx) => (
                                <div className="resource-item" key={m.id}>
                                  <span className="drag-handle-icon">⠿</span>
                                  <span className="resource-num">{rIdx + 1}</span>
                                  <div className="resource-info">
                                    <h4>{m.title || "—"}</h4>
                                    <p>
                                      {(m.file_type || "File").toString()}{" "}
                                      {m.size ? `· ${formatBytes(m.size)}` : ""}{" "}
                                      {m.created_at ? `· Uploaded ${formatDateShort(m.created_at)}` : ""}
                                    </p>
                                  </div>
                                  {m.media_group ? (
                                    <span className="badge badge-info badge-sm">{mediaGroupLabel(m.media_group)}</span>
                                  ) : (
                                    <span className={`badge ${String(m.reading_type || "priority").toLowerCase() === "optional" ? "badge-warning" : "badge-info"} badge-sm`}>
                                      {String(m.reading_type || "priority").toLowerCase() === "optional" ? "Optional" : "Priority"}
                                    </span>
                                  )}
                                  <span className={`badge ${String(m.status || "active").toLowerCase() === "completed" ? "badge-success" : String(m.status || "active").toLowerCase() === "draft" ? "badge-warning" : "badge-info"} badge-sm`}>
                                    {String(m.status || "active").toLowerCase() === "completed" ? "Completed" : String(m.status || "active").toLowerCase() === "draft" ? "Draft" : "Active"}
                                  </span>
                                  <div className="flex gap-2">
                                    <button className="btn btn-outline btn-sm" type="button" onClick={() => { setActiveWeekSectionId(ws.id); openEditResourceModal(ws, m); }}>
                                      ✏️
                                    </button>
                                    <button className="btn btn-outline btn-sm text-danger" type="button" onClick={() => { setActiveWeekSectionId(ws.id); setCurrentMediaSectionId(ws.id); openDeleteModal("resource", m); }}>
                                      🗑️
                                    </button>
                                  </div>
                                </div>
                              ))}
                            </div>

                            <div className="dashed-placeholder mt-4">
                              <p className="text-13">
                                📎 Drag & drop a file here or click <strong>"+ Add Resource"</strong> above
                              </p>
                              <p className="small-text mt-2">Supports: PDF, PNG, JPG, DOCX · Max 20MB</p>
                            </div>
                          </>
                        ) : displayMode === "grid" ? (
                          <>
                            {editorPlainText(ws?.content) ? (
                              <div className="richtext-placeholder" style={{ marginBottom: 16 }}>
                                <div className="richtext-body medium">
                                  <div dangerouslySetInnerHTML={{ __html: ws?.content || "" }} />
                                </div>
                              </div>
                            ) : null}
                            <p className="help-text">
                              Introductory videos for this week. These are viewed by Adults (Parents) only.
                            </p>
                            <div className="video-grid">
                              {items.map((m) => (
                                <div className="video-grid-item" key={m.id}>
                                  <div className="video-grid-thumb clickable" onClick={() => { setActiveWeekSectionId(ws.id); openVideoPreviewModal(ws, m); }}>
                                    ▶️
                                    {m.duration ? <span className="video-duration">{m.duration}</span> : null}
                                  </div>
                                  <div className="video-grid-info">
                                    <h4>{m.title || "Video"}</h4>
                                    <p>{editorPlainText(m.description) || m.title || "—"}</p>
                                    <div className="video-grid-actions">
                                      <button className="btn btn-outline btn-sm" type="button" onClick={() => { setActiveWeekSectionId(ws.id); openEditVideoModal(ws, m); }}>
                                        ✏️ Edit
                                      </button>
                                      <button className="btn btn-outline btn-sm text-danger" type="button" onClick={() => { setActiveWeekSectionId(ws.id); setCurrentMediaSectionId(ws.id); openDeleteModal("video", m); }}>
                                        🗑️
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>

                            <div className="dashed-placeholder mt-4">
                              <p className="text-13">
                                📹 Drag & drop a video here or click <strong>"+ Add Intro Video"</strong> above
                              </p>
                              <p className="small-text mt-2">Supports: MP4, MOV, WebM · Max 500MB</p>
                            </div>
                          </>
                        ) : displayMode === "list" ? (
                          <>
                            {editorPlainText(ws?.content) ? (
                              <div className="richtext-placeholder" style={{ marginBottom: 16 }}>
                                <div className="richtext-body medium">
                                  <div dangerouslySetInnerHTML={{ __html: ws?.content || "" }} />
                                </div>
                              </div>
                            ) : null}
                            <p className="help-text">
                              Core supplementary videos for this week.
                            </p>
                            <div id={`core-video-list-${wsId}`}>
                              {items.map((m, vIdx) => (
                                <div className="video-item" key={m.id}>
                                  <div className="drag-handle-icon">⠿</div>
                                  <div className="video-thumb clickable" onClick={() => { setActiveWeekSectionId(ws.id); openVideoPreviewModal(ws, m); }}>
                                    ▶️
                                  </div>
                                  <div className="video-info">
                                    <h4>{vIdx + 1}. {m.title || "Core Video"}</h4>
                                    <p>Duration: {m.duration || "—"} {m.created_at ? `· Uploaded ${formatDateShort(m.created_at)}` : ""}</p>
                                    <p className="mt-2"><span className="badge badge-info badge-sm">{m.title || "Core Video"}</span></p>
                                  </div>
                                  <div className="flex gap-2">
                                    <button className="btn btn-outline btn-sm" type="button" onClick={() => { setActiveWeekSectionId(ws.id); openEditVideoModal(ws, m); }}>
                                      ✏️ Edit
                                    </button>
                                    <button className="btn btn-outline btn-sm text-danger" type="button" onClick={() => { setActiveWeekSectionId(ws.id); setCurrentMediaSectionId(ws.id); openDeleteModal("video", m); }}>
                                      🗑️
                                    </button>
                                  </div>
                                </div>
                              ))}
                            </div>
                            <div className="dashed-placeholder mt-3">
                              <p className="text-13">
                                📹 Drag & drop a video here or click <strong>"+ Add Core Video"</strong> above
                              </p>
                              <p className="small-text mt-2">Supports: MP4, MOV, WebM · Max 500MB</p>
                            </div>
                          </>
                        ) : (
                          <>
                            {editorPlainText(ws?.content) ? (
                              <div className="richtext-placeholder" style={{ marginBottom: 16 }}>
                                <div className="richtext-body medium">
                                  <div dangerouslySetInnerHTML={{ __html: ws?.content || "" }} />
                                </div>
                              </div>
                            ) : null}
                            <p className="help-text">
                              The main video for this section. Upload one video and replace it anytime.
                            </p>
                            {items.length ? (
                              <div className="single-video">
                                <div className="single-video-thumb clickable" onClick={() => { setActiveWeekSectionId(ws.id); openVideoPreviewModal(ws, items[0]); }}>
                                  ▶️
                                  {items[0]?.duration ? <span className="video-duration">{items[0].duration}</span> : null}
                                </div>
                                <div className="single-video-info">
                                  <h4>{items[0]?.title || "Video"}</h4>
                                  <p>{editorPlainText(items[0]?.description) || "—"}</p>
                                  <div className="flex gap-2 mt-3">
                                    <button className="btn btn-outline btn-sm" type="button" onClick={() => { setActiveWeekSectionId(ws.id); openEditVideoModal(ws, items[0]); }}>
                                      ✏️ Edit Video
                                    </button>
                                    <button className="btn btn-outline btn-sm text-danger" type="button" onClick={() => { setActiveWeekSectionId(ws.id); setCurrentMediaSectionId(ws.id); openDeleteModal("video", items[0]); }}>
                                      🗑️ Remove
                                    </button>
                                  </div>
                                </div>
                              </div>
                            ) : (
                              <div className="empty-state" style={{ padding: "22px 12px" }}>
                                No video yet.
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </>
      )}

      {/* ============================
          Wireframe Modals
          ============================ */}

      {/* MODAL: Video Preview */}
      {videoPreviewOpen ? (
        <div className="modal-overlay active" id="modal-video-preview" onClick={() => setVideoPreviewOpen(false)}>
          <div className="modal modal-lg" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>📹 Video Preview</h3>
              <button className="modal-close" type="button" onClick={() => setVideoPreviewOpen(false)}>
                ✕
              </button>
            </div>
            <div className="modal-body">
              <div className="video-preview-container">
                <div
                  className="play-circle"
                  role="button"
                  tabIndex={0}
                  onClick={() => {
                    if (videoPreview.url) window.open(videoPreview.url, "_blank", "noreferrer");
                    else toast.info("Video playback is a wireframe placeholder", { toastId: "video-preview" });
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      if (videoPreview.url) window.open(videoPreview.url, "_blank", "noreferrer");
                      else toast.info("Video playback is a wireframe placeholder", { toastId: "video-preview" });
                    }
                  }}
                >
                  ▶
                </div>
              </div>
              <div className="video-preview-info">
                <h4 id="preview-title">{videoPreview.title || "Video Title"}</h4>
                <p id="preview-description">{editorPlainText(videoPreview.description) || "Video description goes here."}</p>
                <div className="meta-row">
                  <span id="preview-duration">⏱ Duration: {videoPreview.duration || "0:00"}</span>
                  <span id="preview-section">📂 Section: {videoPreview.section || "Section"}</span>
                  <span>📅 Uploaded: {videoPreview.uploadedLabel || "—"}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {/* MODAL: Add/Edit Video */}
      {videoModalOpen ? (
        <div
          className="modal-overlay active"
          id="modal-add-video"
          onClick={() => {
            setVideoModalOpen(false);
            setVideoEditingId(null);
            videoEditSourceRef.current = null;
          }}
        >
          <div className="modal modal-lg" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 id="add-video-modal-title">{videoEditingId ? "✏️ Edit Video" : "📹 Add New Video"}</h3>
              <button
                className="modal-close"
                type="button"
                onClick={() => {
                  setVideoModalOpen(false);
                  setVideoEditingId(null);
                  videoEditSourceRef.current = null;
                }}
              >
                ✕
              </button>
            </div>
            <div className="modal-body">
              <div className="form-row">
                <div className="form-group">
                  <label>Video Title *</label>
                  <input
                    type="text"
                    className="form-control"
                    id="video-title-input"
                    placeholder="e.g. Theory, Activities, Parent Guide..."
                    value={videoForm.title}
                    onChange={(e) => setVideoForm((p) => ({ ...p, title: e.target.value }))}
                  />
                </div>
                <div className="form-group">
                  <label>Video Type</label>
                  <select
                    className="form-control"
                    id="video-type-input"
                    value={videoTypeFormValue(videoForm.type)}
                    onChange={(e) => setVideoForm((p) => ({ ...p, type: videoTypeFormValue(e.target.value) }))}
                  >
                    <option value="1">Intro Video</option>
                    <option value="2">Course Video</option>
                    <option value="3">Core Video</option>
                  </select>
            
                </div>
              </div>
              <div className="form-group">
                <label>Description</label>
                <CKEditorWithSource
                  heightClassName="ckeditor-height-200"
                  containerStyle={{ border: "1px solid var(--gray-300)", borderRadius: 6, overflow: "hidden" }}
                  config={{ extraPlugins: [CkUploadAdapterPlugin] }}
                  data={videoForm.description}
                  onChange={(html) => setVideoForm((p) => ({ ...p, description: html }))}
                />
              </div>
              <div className="form-group">
                <label>Media provider *</label>
                <select
                  className="form-control"
                  id="video-provider-select"
                  value={videoForm.provider === "Thirdparty" ? "Thirdparty" : "Selfhosted"}
                  onChange={(e) => {
                    const v = e.target.value === "Thirdparty" ? "Thirdparty" : "Selfhosted";
                    setVideoForm((p) => ({ ...p, provider: v, file_url: v === "Thirdparty" ? p.file_url : "" }));
                    if (v === "Thirdparty") setVideoFile(null);
                  }}
                >
                  <option value="Selfhosted">Self-hosted</option>
                  <option value="Thirdparty">Third-party</option>
                </select>
              </div>
              {videoForm.provider === "Thirdparty" ? (
                <div className="form-group">
                  <label>Video URL *</label>
                  <input
                    type="text"
                    className="form-control"
                    id="video-external-url-input"
                    placeholder="https://… (embed or direct video link)"
                    value={videoForm.file_url}
                    onChange={(e) => setVideoForm((p) => ({ ...p, file_url: e.target.value }))}
                  />
                  <p className="small-text mt-2">Paste a link to your hosted video (e.g. Vimeo, YouTube, or direct file URL).</p>
                </div>
              ) : (
                <div className="form-group">
                  <label>Upload Video File *</label>
                  <div className="upload-zone" role="button" tabIndex={0} onClick={() => videoFileRef.current?.click?.()} onKeyDown={(e) => { if (e.key === "Enter") videoFileRef.current?.click?.(); }}>
                    <div className="upload-zone-icon">📹</div>
                    <p>{videoFile ? videoFile.name : "Click to upload or drag & drop"}</p>
                    <p className="upload-zone-hint">MP4, MOV, WebM · Max 500MB</p>
                  </div>
                  <input ref={videoFileRef} type="file" accept="video/*" style={{ display: "none" }} onChange={(e) => setVideoFile(e.target.files && e.target.files[0] ? e.target.files[0] : null)} />
                  {videoEditingId && !videoFile && videoExistingFileUrl ? (
                    <div className="small-text mt-2">
                      Current video:{" "}
                      <a className="action-link" href={videoExistingFileUrl} target="_blank" rel="noreferrer">
                        Open video link
                      </a>
                    </div>
                  ) : null}
                </div>
              )}
              <div className="form-row">
                <div className="form-group">
                  <label>Thumbnail (optional)</label>
                  <div className="upload-zone" role="button" tabIndex={0} style={{ padding: 16 }} onClick={() => videoThumbRef.current?.click?.()} onKeyDown={(e) => { if (e.key === "Enter") videoThumbRef.current?.click?.(); }}>
                    <div className="upload-zone-icon">🖼️</div>
                    <p>{videoThumb ? videoThumb.name : "Upload thumbnail"}</p>
                    <p className="upload-zone-hint">PNG, JPG · Recommended 1280x720</p>
                  </div>
                  <input ref={videoThumbRef} type="file" accept="image/*" style={{ display: "none" }} onChange={(e) => setVideoThumb(e.target.files && e.target.files[0] ? e.target.files[0] : null)} />
                  {videoEditingId && !videoThumb && videoExistingThumbUrl ? (
                    <div className="mt-2">
                      <div className="small-text" style={{ marginBottom: 6 }}>Current thumbnail:</div>
                      <img
                        src={videoExistingThumbUrl}
                        alt="Current thumbnail"
                        style={{ width: "100%", maxHeight: 160, objectFit: "cover", borderRadius: 8, border: "1px solid var(--gray-200)" }}
                      />
                    </div>
                  ) : null}
                </div>
                <div className="form-group">
                  <label>Duration</label>
                  <input
                    type="text"
                    className="form-control"
                    id="video-duration-input"
                    placeholder="e.g. 5:35"
                    value={videoForm.duration}
                    onChange={(e) => setVideoForm((p) => ({ ...p, duration: e.target.value }))}
                  />
                  <p className="small-text mt-2">Auto-detected after upload</p>
                </div>
                <div className="form-group">
                  <label>Video Orientation</label>
                  <select
                    className="form-control"
                    id="video-orientation-input"
                    value={videoOrientationFormValue(videoForm.video_orientation)}
                    onChange={(e) => setVideoForm((p) => ({ ...p, video_orientation: videoOrientationFormValue(e.target.value) }))}
                  >
                    <option value="landscape">Landscape</option>
                    <option value="portrait">Portrait</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Position</label>
                  <input
                    type="text"
                    inputMode="numeric"
                    className="form-control"
                    id="video-position-input"
                    placeholder="0"
                    autoComplete="off"
                    value={videoForm.position}
                    onChange={(e) =>
                      setVideoForm((p) => ({ ...p, position: sectionMediaPositionDigitsOnly(e.target.value) }))
                    }
                  />
                  <p className="small-text mt-2">Sort order (non-negative whole numbers only). Leave empty for 0.</p>
                </div>
                <div className="form-group">
                  <label>Media group (preview hub)</label>
                  <select
                    className="form-control"
                    value={videoForm.mediaGroup || ""}
                    onChange={(e) => setVideoForm((p) => ({ ...p, mediaGroup: e.target.value }))}
                  >
                    {VIDEO_MEDIA_GROUPS.map((opt) => (
                      <option key={opt.value || "none"} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                  <p className="small-text mt-2">Leave empty for the live app. Tag intro / hub hero videos so users 149 and 150 get the new layout.</p>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button
                className="btn btn-outline"
                type="button"
                onClick={() => {
                  setVideoModalOpen(false);
                  setVideoEditingId(null);
                  videoEditSourceRef.current = null;
                }}
              >
                Cancel
              </button>
              <button className="btn btn-primary" id="save-video-btn" type="button" onClick={saveVideo}>
                <>
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                    aria-hidden="true"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2Z" />
                    <path d="M17 21v-8H7v8" />
                    <path d="M7 3v5h8" />
                  </svg>
                  {videoEditingId ? "Update Video" : "Save Video"}
                </>
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {/* MODAL: Add/Edit Resource */}
      {resourceModalOpen ? (
        <div
          className="modal-overlay active"
          id="modal-resource"
          onClick={() => {
            setResourceModalOpen(false);
            setResourceFieldError("");
          }}
        >
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 id="resource-modal-title">{resourceEditingId ? "✏️ Edit Resource" : "📎 Add New Resource"}</h3>
              <button
                className="modal-close"
                type="button"
                onClick={() => {
                  setResourceModalOpen(false);
                  setResourceEditingId(null);
                  setResourceFieldError("");
                }}
              >
                ✕
              </button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Resource Name *</label>
                <input
                  type="text"
                  className="form-control"
                  id="resource-name-input"
                  placeholder="e.g. Emotion Wheel Chart, Activity Sheet..."
                  value={resourceForm.name}
                  onChange={(e) => setResourceForm((p) => ({ ...p, name: e.target.value }))}
                />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>File Type</label>
                  <select
                    className="form-control"
                    id="resource-type-input"
                    value={resourceForm.fileType}
                    onChange={(e) => setResourceForm((p) => ({ ...p, fileType: e.target.value }))}
                  >
                    <option value="PDF">PDF</option>
                    <option value="Image">Image (PNG/JPG)</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Status</label>
                  <select
                    className="form-control"
                    id="resource-status-input"
                    value={resourceForm.status}
                    onChange={(e) => setResourceForm((p) => ({ ...p, status: e.target.value }))}
                  >
                    <option value="Active">Active</option>
                    <option value="Completed">Completed</option>
                    <option value="Draft">Draft</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Position</label>
                  <input
                    type="text"
                    inputMode="numeric"
                    className="form-control"
                    id="resource-position-input"
                    placeholder="0"
                    autoComplete="off"
                    value={resourceForm.position}
                    onChange={(e) =>
                      setResourceForm((p) => ({ ...p, position: sectionMediaPositionDigitsOnly(e.target.value) }))
                    }
                  />
                  <p className="small-text mt-2">Sort order (numbers only). Leave empty for 0.</p>
                </div>
                <div className="form-group">
                  <label>Reading type</label>
                  <select
                    className="form-control"
                    id="resource-reading-type-input"
                    value={resourceForm.readingType || "priority"}
                    onChange={(e) => setResourceForm((p) => ({ ...p, readingType: e.target.value }))}
                  >
                    <option value="priority">Priority reading</option>
                    <option value="optional">Optional reading</option>
                  </select>
                  <p className="small-text mt-2">Priority shows on page 1. Optional shows on More Resources (page 2).</p>
                </div>
                <div className="form-group">
                  <label>Media group (preview hub)</label>
                  <select
                    className="form-control"
                    id="resource-media-group-input"
                    value={resourceForm.mediaGroup || ""}
                    onChange={(e) => setResourceForm((p) => ({ ...p, mediaGroup: e.target.value }))}
                  >
                    {RESOURCE_MEDIA_GROUPS.map((opt) => (
                      <option key={opt.value || "none"} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                  <p className="small-text mt-2">Overrides reading type for preview hub buttons. Leave empty to keep live priority/optional behaviour.</p>
                </div>
              </div>
              {resourceForm.fileType === "PDF" ? (
                <div className="form-group">
                  <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
                    <input
                      type="checkbox"
                      id="resource-is-editable-input"
                      checked={!!resourceForm.isEditable}
                      onChange={(e) => setResourceForm((p) => ({ ...p, isEditable: e.target.checked }))}
                    />
                    <span>Editable certificate (user can edit fields)</span>
                  </label>
                </div>
              ) : null}
              <div className="form-group">
                <label>Upload File *</label>
                <div
                  className="upload-zone"
                  role="button"
                  tabIndex={0}
                  onClick={() => resourceFileRef.current?.click?.()}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") resourceFileRef.current?.click?.();
                  }}
                >
                  <div className="upload-zone-icon">📎</div>
                  <p>{resourceFile ? resourceFile.name : "Click to upload or drag & drop"}</p>
                  <p className="upload-zone-hint">PDF, PNG, JPG · Max 20MB</p>
                </div>
                <input
                  ref={resourceFileRef}
                  type="file"
                  style={{ display: "none" }}
                  accept={
                    resourceForm.fileType === "Image"
                      ? ".png,.jpg,.jpeg,image/png,image/jpg,image/jpeg"
                      : resourceForm.fileType === "PDF"
                      ? ".pdf,application/pdf"
                      : undefined
                  }
                  onChange={e => {
                    const file = e.target.files && e.target.files[0] ? e.target.files[0] : null;
                    let isValid = false;
                    let errorMsg = "";
                    if (file) {
                      if (resourceForm.fileType === "PDF") {
                        const validMime = file.type === "application/pdf";
                        const validExt = /\.pdf$/i.test(file.name);
                        if (validMime && validExt) {
                          isValid = true;
                        } else {
                          errorMsg = "Selected file must be a valid PDF file.";
                        }
                      } else if (resourceForm.fileType === "Image") {
                        const validMime =
                          file.type === "image/png" ||
                          file.type === "image/jpeg" ||
                          file.type === "image/jpg";
                        const validExt = /\.(png|jpg|jpeg)$/i.test(file.name);
                        if (validMime && validExt) {
                          isValid = true;
                        } else {
                          errorMsg = "Selected file must be a valid PNG or JPG image.";
                        }
                      } else {
                        // fallback allow
                        isValid = true;
                      }
                    }
                    if (file && !isValid) {
                      setResourceFile(null);
                      setResourceFieldError(errorMsg || "Invalid file type selected."); // <--- set visible error
                      // window.toast?.error?.(errorMsg || "Invalid file type selected.", { toastId: "resource-file" });
                      e.target.value = "";
                      return;
                    }
                    setResourceFile(file);
                    setResourceFieldError(""); // clear the error if any
                  }}
                />
                {/* Show file error below upload zone */}
                {resourceFieldError && (
                  <div className="small-label" style={{ marginTop: 6, color: "var(--danger)" }}>
                    {resourceFieldError}
                  </div>
                )}
                {resourceEditingId && !resourceFile && resourceExistingFileUrl ? (
                  <div className="mt-2">
                    {resourceExistingIsImage ? (
                      <>
                        <div className="small-text" style={{ marginBottom: 6 }}>Current image:</div>
                        <img
                          src={resourceExistingFileUrl}
                          alt="Current resource"
                          style={{
                            width: "100%",
                            maxHeight: 200,
                            objectFit: "contain",
                            borderRadius: 8,
                            border: "1px solid var(--gray-200)",
                            background: "var(--gray-50)"
                          }}
                        />
                      </>
                    ) : (
                      <div className="small-text">
                        Current document:{" "}
                        <a className="action-link" href={resourceExistingFileUrl} target="_blank" rel="noreferrer">
                          Open file link
                        </a>
                      </div>
                    )}
                  </div>
                ) : null}
              </div>
              <div className="form-group">
                <label>Description (optional)</label>
                <CKEditorWithSource
                  heightClassName="ckeditor-height-200"
                  containerStyle={{ border: "1px solid var(--gray-300)", borderRadius: 6, overflow: "hidden" }}
                  config={{ extraPlugins: [CkUploadAdapterPlugin] }}
                  data={resourceForm.description}
                  onChange={(html) => setResourceForm((p) => ({ ...p, description: html }))}
                />
              </div>
            </div>
            <div className="modal-footer">
              <button
                className="btn btn-outline"
                type="button"
                onClick={() => {
                  setResourceModalOpen(false);
                  setResourceEditingId(null);
                  setResourceFieldError("");
                }}
              >
                Cancel
              </button>
              <button className="btn btn-primary" id="save-resource-btn" type="button" onClick={saveResource}>
                <>
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                    aria-hidden="true"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2Z" />
                    <path d="M17 21v-8H7v8" />
                    <path d="M7 3v5h8" />
                  </svg>
                  {resourceEditingId ? "Update Resource" : "Save Resource"}
                </>
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {/* MODAL: Delete Confirmation */}
      {deleteModalOpen ? (
        <div className="modal-overlay active" id="modal-delete" onClick={() => setDeleteModalOpen(false)}>
          <div className="modal modal-sm" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>⚠️ Confirm Delete</h3>
              <button className="modal-close" type="button" onClick={() => setDeleteModalOpen(false)}>
                ✕
              </button>
            </div>
            <div className="modal-body">
              <div className="delete-confirm-body">
                <div className="delete-icon">🗑️</div>
                <h4 id="delete-title">Delete this {deleteTarget.kind || "item"}?</h4>
                <p id="delete-message">
                  Are you sure you want to delete "<strong id="delete-item-name">{deleteTarget.name || "Item Name"}</strong>"? This action cannot be undone.
                </p>
              </div>
            </div>
            <div className="modal-footer center">
              <button className="btn btn-outline" type="button" onClick={() => setDeleteModalOpen(false)}>
                Cancel
              </button>
              <button className="btn btn-danger" id="confirm-delete-btn" type="button" onClick={confirmDelete}>
                🗑️ Delete
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {addSectionModalOpen ? (
        <div
          className="modal-overlay active"
          role="dialog"
          aria-modal="true"
          onClick={() => setAddSectionModalOpen(false)}
        >
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Add Section</h3>
              <button type="button" className="modal-close" onClick={() => setAddSectionModalOpen(false)}>
                ✕
              </button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Section Title</label>
                <input
                  className="form-control"
                  value={addSectionForm.title}
                  onChange={(e) => setAddSectionForm((p) => ({ ...p, title: e.target.value }))}
                  disabled={loadingAvailableSections}
                  placeholder={loadingAvailableSections ? "Loading..." : "Type section title..."}
                  list="week-sections-datalist-add"
                />
                <datalist id="week-sections-datalist-add">
                  {weekSectionDatalistSections.map((s) => (
                    <option key={s.id} value={s.title} />
                  ))}
                </datalist>
              </div>

              <div className="form-group">
                <label>Section Type</label>
                <select
                  className="form-control"
                  value={addSectionForm.type}
                  onChange={(e) => {
                    const nextType = e.target.value;
                    setAddSectionForm((p) => ({
                      ...p,
                      type: nextType,
                      file_url: nextType === "content" ? p.file_url : "",
                      visibility: defaultVisibilityForType(nextType, p.visibility)
                    }));
                    if (nextType !== "content") {
                      setAddSectionPdfFile(null);
                      setAddSectionPdfError("");
                    }
                  }}
                >
                  {sectionTypeOptions()}
                </select>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Display subtitle (optional)</label>
                  <input
                    className="form-control"
                    value={addSectionForm.display_subtitle || ""}
                    onChange={(e) => setAddSectionForm((p) => ({ ...p, display_subtitle: e.target.value }))}
                    placeholder="Optional. Shown instead of audience subtitle for 149/150."
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Audience Key</label>
                <select
                  className="form-control"
                  value={addSectionForm.audience}
                  onChange={(e) => setAddSectionForm((p) => ({ ...p, audience: e.target.value }))}
                >
                  <option value="Both">Both (A + K)</option>
                  <option value="Adult">Adult (A)</option>
                  <option value="Kids">Kids (K)</option>
                </select>
              </div>

              <div className="form-group" style={{ marginBottom: 20 }}>
                <label className="flex gap-2 items-center" style={{ cursor: "pointer", fontWeight: 600 }}>
                  <input
                    type="checkbox"
                    checked={!!addSectionForm.is_core_videos}
                    onChange={(e) => setAddSectionForm((p) => ({ ...p, is_core_videos: e.target.checked }))}
                  />
                  <span> Core videos</span>
                </label>               
              </div>

              {String(addSectionForm.type || "").toLowerCase() === "videos" ? (
                <div className="form-group">
                  <label>Video Type</label>
                  <select
                    className="form-control"
                    value={addSectionForm.video_type}
                    onChange={(e) => setAddSectionForm((p) => ({ ...p, video_type: e.target.value }))}
                  >
                    <option value="">Select Video Type</option>
                    <option value="1">Intro Video</option>
                    <option value="2">Course Video</option>
                    <option value="3">Core Video</option>
                  </select>
                </div>
              ) : null}

              <div className="form-group" style={{ marginBottom: 20 }}>
                <label className="flex gap-2 items-center" style={{ cursor: "pointer", fontWeight: 600 }}>
                  <input
                    type="checkbox"
                    checked={!!addSectionForm.is_complete_section}
                    onChange={(e) => setAddSectionForm((p) => ({ ...p, is_complete_section: e.target.checked }))}
                  />
                  <span> Mark as complete section</span>
                </label>
              </div>

              <div className="form-group">
                <label>Content</label>
                <p className="small-label" style={{ marginBottom: 8 }}>
                  Rich text for this section (shown for Content-type sections; optional intro text for Videos / Resources).
                </p>
                <CKEditorWithSource
                  heightClassName="ckeditor-height-200"
                  containerStyle={{ border: "1px solid var(--gray-300)", borderRadius: 6, overflow: "hidden" }}
                  config={{ extraPlugins: [CkUploadAdapterPlugin] }}
                  data={addSectionForm.content}
                  onChange={(html) => setAddSectionForm((p) => ({ ...p, content: html }))}
                />
              </div>

              {String(addSectionForm.type || "").toLowerCase() === "content" ? (
                <div className="form-group">
                  <label>
                    Upload PDF <span className="text-danger">*</span>
                  </label>
                  <p className="small-label" style={{ marginBottom: 8 }}>
                    Required for Content section type. Only PDF is accepted.
                  </p>
                  <input
                    ref={addSectionPdfInputRef}
                    type="file"
                    accept=".pdf,application/pdf"
                    style={{ display: "none" }}
                    onChange={(e) => {
                      const f = e.target.files && e.target.files[0] ? e.target.files[0] : null;
                      if (f && !isValidPdf(f)) {
                        setAddSectionPdfFile(null);
                        setAddSectionPdfError("Only PDF files are allowed.");
                        e.target.value = "";
                        return;
                      }
                      setAddSectionPdfFile(f);
                      setAddSectionPdfError("");
                      if (f) setAddSectionForm((p) => ({ ...p, file_url: "" }));
                    }}
                  />
                  <div className="flex gap-3 items-center" style={{ flexWrap: "wrap" }}>
                    <button
                      type="button"
                      className="btn btn-outline btn-sm"
                      onClick={() => addSectionPdfInputRef.current?.click?.()}
                    >
                      {addSectionPdfFile ? "Change PDF" : "Upload PDF"}
                    </button>
                    {addSectionPdfFile ? <span className="small-text">{addSectionPdfFile.name}</span> : null}
                    {addSectionForm.file_url ? (
                      <a className="action-link" href={resolvePublicUrl(addSectionForm.file_url)} target="_blank" rel="noreferrer">
                        Current PDF
                      </a>
                    ) : null}
                  </div>
                  {addSectionPdfError ? (
                    <div className="small-label" style={{ marginTop: 6, color: "var(--danger)" }}>
                      {addSectionPdfError}
                    </div>
                  ) : null}
                </div>
              ) : null}

              <div className="form-group">
                <label>Thumbnail URL</label>
                <p className="small-label" style={{ marginBottom: 8 }}>
                  Optional image for this section (upload or leave empty).
                </p>
                <input
                  ref={addSectionThumbInputRef}
                  type="file"
                  accept="image/*"
                  style={{ display: "none" }}
                  onChange={(e) => {
                    const file = e.target.files && e.target.files[0] ? e.target.files[0] : null;
                    if (file) uploadSectionThumbnail(file, setAddSectionForm, addSectionThumbInputRef);
                  }}
                />
                <div className="flex gap-3 items-center" style={{ flexWrap: "wrap" }}>
                  <button
                    type="button"
                    className="btn btn-outline btn-sm"
                    disabled={uploadingSectionThumb || loadingAvailableSections}
                    onClick={() => addSectionThumbInputRef.current && addSectionThumbInputRef.current.click()}
                  >
                    {uploadingSectionThumb ? "Uploading…" : addSectionForm.thumbnail_url ? "Change thumbnail" : "Upload thumbnail"}
                  </button>
                  {addSectionForm.thumbnail_url ? (
                    <button
                      type="button"
                      className="btn btn-outline btn-sm"
                      disabled={uploadingSectionThumb || loadingAvailableSections}
                      onClick={() => setAddSectionForm((p) => ({ ...p, thumbnail_url: "" }))}
                    >
                      Remove
                    </button>
                  ) : null}
                  {addSectionForm.thumbnail_url ? (
                    <img
                      src={resolvePublicUrl(addSectionForm.thumbnail_url)}
                      alt="Thumbnail preview"
                      style={{ width: 56, height: 56, objectFit: "cover", borderRadius: 8, border: "1px solid var(--gray-300)" }}
                    />
                  ) : null}
                </div>
                <input
                  className="form-control"
                  style={{ marginTop: 10 }}
                  value={addSectionForm.thumbnail_url}
                  onChange={(e) => setAddSectionForm((p) => ({ ...p, thumbnail_url: e.target.value }))}
                  placeholder="Or paste image URL…"
                />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-primary" type="button" onClick={saveNewSection} disabled={loadingAvailableSections}>
                Save
              </button>
              <button className="btn btn-outline" type="button" onClick={() => setAddSectionModalOpen(false)}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {editSectionModalOpen && editingWeekSection ? (
        <div
          className="modal-overlay active"
          role="dialog"
          aria-modal="true"
          onClick={() => {
            setEditSectionModalOpen(false);
            setEditingWeekSectionId(null);
          }}
        >
          <div className="modal modal-lg" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Edit Section</h3>
              <button
                type="button"
                className="modal-close"
                onClick={() => {
                  setEditSectionModalOpen(false);
                  setEditingWeekSectionId(null);
                }}
              >
                ✕
              </button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Section Title</label>
                <input
                  className="form-control"
                  value={editSectionForm.title}
                  onChange={(e) => setEditSectionForm((p) => ({ ...p, title: e.target.value }))}
                  placeholder="Type section title..."
                  list="week-sections-datalist-edit"
                />
                <datalist id="week-sections-datalist-edit">
                  {weekSectionDatalistSections.map((s) => (
                    <option key={s.id} value={s.title} />
                  ))}
                </datalist>
              </div>

              <div className="form-group">
                <label>Section Type</label>
                <select
                  className="form-control"
                  value={editSectionForm.type}
                  onChange={(e) => {
                    const nextType = e.target.value;
                    setEditSectionForm((p) => ({
                      ...p,
                      type: nextType,
                      file_url: nextType === "content" ? p.file_url : "",
                      visibility: defaultVisibilityForType(nextType, p.visibility)
                    }));
                    if (nextType !== "content") {
                      setEditSectionPdfFile(null);
                      setEditSectionPdfError("");
                    }
                  }}
                >
                  {sectionTypeOptions()}
                </select>
              </div>
              
              <div className="form-row">
                <div className="form-group">
                  <label>Display subtitle</label>
                  <input
                    className="form-control"
                    value={editSectionForm.display_subtitle || ""}
                    onChange={(e) => setEditSectionForm((p) => ({ ...p, display_subtitle: e.target.value }))}
                    placeholder="Optional. Shown instead of audience subtitle for 149/150."
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Audience Key</label>
                <select
                  className="form-control"
                  value={editSectionForm.audience}
                  onChange={(e) => setEditSectionForm((p) => ({ ...p, audience: e.target.value }))}
                >
                  <option value="Both">Both (A + K)</option>
                  <option value="Adult">Adult (A)</option>
                  <option value="Kids">Kids (K)</option>
                </select>
              </div>

              <div className="form-group" style={{ marginBottom: 20 }}>
                <label className="flex gap-2 items-center" style={{ cursor: "pointer", fontWeight: 600 }}>
                  <input
                    type="checkbox"
                    checked={!!editSectionForm.is_core_videos}
                    onChange={(e) => setEditSectionForm((p) => ({ ...p, is_core_videos: e.target.checked }))}
                  />
                  <span> Core videos</span>
                </label>
              
              </div>

              {String(editSectionForm.type || "").toLowerCase() === "videos" ? (
                <div className="form-group">
                  <label>Video Type</label>
                  <select
                    className="form-control"
                    value={editSectionForm.video_type}
                    onChange={(e) => setEditSectionForm((p) => ({ ...p, video_type: e.target.value }))}
                  >
                    <option value="">Select Video Type</option>
                    <option value="1">Intro Video</option>
                    <option value="2">Course Video</option>
                    <option value="3">Core Video</option>
                  </select>
                </div>
              ) : null}

              <div className="form-group" style={{ marginBottom: 20 }}>
                <label className="flex gap-2 items-center" style={{ cursor: "pointer", fontWeight: 600 }}>
                  <input
                    type="checkbox"
                    checked={!!editSectionForm.is_complete_section}
                    onChange={(e) => setEditSectionForm((p) => ({ ...p, is_complete_section: e.target.checked }))}
                  />
                  <span> Mark as complete section</span>
                </label>
              </div>

              <div className="form-group">
                <label>Content</label>
                <p className="small-label" style={{ marginBottom: 8 }}>
                  Rich text for this section (shown for Content-type sections; optional intro text for Videos / Resources).
                </p>
                <CKEditorWithSource
                  heightClassName="ckeditor-height-200"
                  containerStyle={{ border: "1px solid var(--gray-300)", borderRadius: 6, overflow: "hidden" }}
                  config={{ extraPlugins: [CkUploadAdapterPlugin] }}
                  data={editSectionForm.content}
                  onChange={(html) => setEditSectionForm((p) => ({ ...p, content: html }))}
                />
              </div>

              {String(editSectionForm.type || "").toLowerCase() === "content" ? (
                <div className="form-group">
                  <label>
                    Upload PDF <span className="text-danger">*</span>
                  </label>
                  <p className="small-label" style={{ marginBottom: 8 }}>
                    Required for Content section type. Only PDF is accepted.
                  </p>
                  <input
                    ref={editSectionPdfInputRef}
                    type="file"
                    accept=".pdf,application/pdf"
                    style={{ display: "none" }}
                    onChange={(e) => {
                      const f = e.target.files && e.target.files[0] ? e.target.files[0] : null;
                      if (f && !isValidPdf(f)) {
                        setEditSectionPdfFile(null);
                        setEditSectionPdfError("Only PDF files are allowed.");
                        e.target.value = "";
                        return;
                      }
                      setEditSectionPdfFile(f);
                      setEditSectionPdfError("");
                      if (f) setEditSectionForm((p) => ({ ...p, file_url: "" }));
                    }}
                  />
                  <div className="flex gap-3 items-center" style={{ flexWrap: "wrap" }}>
                    <button
                      type="button"
                      className="btn btn-outline btn-sm"
                      onClick={() => editSectionPdfInputRef.current?.click?.()}
                    >
                      {editSectionPdfFile ? "Change PDF" : "Upload PDF"}
                    </button>
                    {editSectionPdfFile ? <span className="small-text">{editSectionPdfFile.name}</span> : null}
                    {editSectionForm.file_url ? (
                      <a className="action-link" href={resolvePublicUrl(editSectionForm.file_url)} target="_blank" rel="noreferrer">
                        Current PDF
                      </a>
                    ) : null}
                  </div>
                  {editSectionPdfError ? (
                    <div className="small-label" style={{ marginTop: 6, color: "var(--danger)" }}>
                      {editSectionPdfError}
                    </div>
                  ) : null}
                </div>
              ) : null}

              <div className="form-group mb-0">
                <label>Thumbnail URL</label>
                <p className="small-label" style={{ marginBottom: 8 }}>
                  Optional image for this section (upload or paste URL).
                </p>
                <input
                  ref={editSectionThumbInputRef}
                  type="file"
                  accept="image/*"
                  style={{ display: "none" }}
                  onChange={(e) => {
                    const file = e.target.files && e.target.files[0] ? e.target.files[0] : null;
                    if (file) uploadSectionThumbnail(file, setEditSectionForm, editSectionThumbInputRef);
                  }}
                />
                <div className="flex gap-3 items-center" style={{ flexWrap: "wrap" }}>
                  <button
                    type="button"
                    className="btn btn-outline btn-sm"
                    disabled={uploadingSectionThumb || savingSection}
                    onClick={() => editSectionThumbInputRef.current && editSectionThumbInputRef.current.click()}
                  >
                    {uploadingSectionThumb ? "Uploading…" : editSectionForm.thumbnail_url ? "Change thumbnail" : "Upload thumbnail"}
                  </button>
                  {editSectionForm.thumbnail_url ? (
                    <button
                      type="button"
                      className="btn btn-outline btn-sm"
                      disabled={uploadingSectionThumb || savingSection}
                      onClick={() => setEditSectionForm((p) => ({ ...p, thumbnail_url: "" }))}
                    >
                      Remove
                    </button>
                  ) : null}
                  {editSectionForm.thumbnail_url ? (
                    <img
                      src={resolvePublicUrl(editSectionForm.thumbnail_url)}
                      alt="Thumbnail preview"
                      style={{ width: 56, height: 56, objectFit: "cover", borderRadius: 8, border: "1px solid var(--gray-300)" }}
                    />
                  ) : null}
                </div>
                <input
                  className="form-control"
                  style={{ marginTop: 10 }}
                  value={editSectionForm.thumbnail_url}
                  onChange={(e) => setEditSectionForm((p) => ({ ...p, thumbnail_url: e.target.value }))}
                  placeholder="Or paste image URL…"
                />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-primary" type="button" disabled={savingSection} onClick={saveEditedSection}>
                {savingSection ? "Saving…" : "Save"}
              </button>
              <button
                className="btn btn-outline"
                type="button"
                onClick={() => {
                  setEditSectionModalOpen(false);
                  setEditingWeekSectionId(null);
                }}
                disabled={savingSection}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

